// Purpose: getting and setting subtree and Elements within it.

// Imports from:
// Any except ContentEditorStoreUtils

import {isEqual} from 'lodash';
import {Json, getObject} from '@packages/utilities/json';
import {Pair, pair, getFirst, getSecond} from '@packages/utilities/Pair';
import {append} from '@packages/utilities/array';
import {insert} from '@packages/utilities/string-utils';
import {clamp} from '@packages/systems/core/utils/NumberUtils';
import {
  parseEmailAddress,
  parseEmailSubject,
  parseTel,
} from '@packages/systems/core/utils/UrlUtils';
import {getElementBindingDataPaths} from '@packages/systems/dynamo/utils/BindingUtils';
import {
  getAncestorIds,
  getKeyPathToElement,
  findElementDescendant,
  getChildren,
  flatten,
  getClosestListItemElement,
  getPrev,
  RelativePositionPlacementOptions,
  addElementAtAnchor,
  removeElementById,
  addElementAtAnchorById,
  getExprElementId,
  updateElementListAtPath,
  filterElementTree,
  getClosestFigureElement,
  replaceElementById,
  reduceElementTree,
  findElement,
  findFurthestAncestor,
  updateElementChildren,
  findAllDescendantsFromExpression,
  findIndexById,
  getElementWithAncestors,
  isUserComponent,
} from '@packages/utilities/platform';
import {updateElementData as updateData} from '@packages/utilities/platform-elementdata';
import {
  elementTypeEquals,
  isTypographic,
  isRichText,
  isDivBlock,
  isHeading,
  isImageElement,
  isIframeElement,
  isFigure,
  isFigCaption,
  isLink,
  isEmbed,
  isListItem,
  isList,
  isLineBreakElement,
  isStringElement,
  isParagraph,
  isCodeBlock,
  isLinkElement,
} from '@packages/utilities/elementTypeUtils';
import {
  EBoolean,
  EElement,
  EList,
  ELiteral,
  Expression,
  EText,
  EEnum,
  ERecord,
} from '@packages/systems/wfdl-ast';
import {
  Expressions,
  ExpressionPath,
  Component,
  Plugins,
  matchExpression,
} from '@packages/systems/wfdl';
import {
  qualify,
  namesEqual,
  Qualified,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {incrementGuid} from '@packages/utilities/guid';
import {
  pipe,
  identity,
  mapArray,
  emptyArray,
  emptyObject,
} from '@packages/utilities/fp/utils';
import {dissoc} from '@packages/utilities/key-map';
import {maybe} from '@packages/utilities/fp/option';
import {LinkToolbarChanges} from '@packages/shared/types/ContentEditor';
import {
  TOOLBARS,
  CONSECUTIVE_SPACE_ATTEMPT_WARNING_THRESHOLD,
  DEFAULT_VIDEO_PADDING_BOTTOM,
} from '@packages/shared/constants/ContentEditor';
import {
  ContentEditorActive,
  PendingFormatToggle,
  activeContentEditor,
} from '@packages/shared/states';

import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  UniqueIdsState,
  instantiateExpressionWithUniqueIds,
} from '@packages/utilities/platform-instantiateExpressionWithUniqueIds';
import {
  type SelectionRecordType,
  NodeSelectionRecord,
  RangeSelectionRecord,
  RangeSelectionRecordType,
} from '@packages/shared/records';
import {
  NBSP_RAW,
  ZWJ_RAW,
  isEmptyValue,
  removeZWJChars,
  cleanZWJChars,
} from '@packages/systems/core/utils/TextUtils';

import type {ContentEditorActiveAndNextId, ElementAndId} from './types';
import type {RichTextComponentSource} from '@packages/shared/types/ContentEditor';
import {
  assertNodeSelectionRecordType,
  assertRangeSelectionRecordType,
  bumpSelectionEdgesInside,
  collapseTextSelection,
  getLeftRightPosition,
  setLeftEndOfSelection,
  setRightEndOfSelection,
  selectText,
  selectionHasTextLevelNodesToWrap,
  getSelectedElements,
  getSelectedTextAndLineBreakNodes,
  getNodeIdFromSelection,
  getNodeIdFromSelectionAtLeft,
  getNodeIdFromSelectionAtRight,
  moveRightSideOfSelection,
  moveSelectionEdgesWithLineBreaks,
  reifySelectionRecord,
  isCollapsed,
  getCharBeforeCaret,
  getCharAfterCaret,
  changeCaretOffsetBy,
  setSelection,
  selectNode,
} from './ContentEditorSelectionUtils';
import {
  headingFactory,
  blockquoteFactory,
  createLineBreak,
  createListItem,
  createParagraph,
  createParagraphWithZwj,
  createStringElement,
  formatElementCreation,
  instantiateElement,
  listFactory,
} from './ContentEditorElementCreation';
import {
  linkType,
  spanType,
  strongType,
  emphasizedType,
  superscriptType,
  subscriptType,
  inlineCodeType,
  headingType,
  blockquoteType,
  paragraphType,
  isFigcaptionFormatElement,
  isPotentialRichTextTopLevelElement,
  isToolbarFormatType,
  contentEditorGetElement,
  getClosestTopLevelElement,
  getClosestTopLevelTypoElement,
  findDeepFigureChildElement,
  isRichTextTopLevelElement,
  isUnmergeableElement,
  isFormatElement,
  isValidParent,
  isValidTextElementParent,
  isSimpleTextElement,
  isFormatOrTextElement,
  getLeftDeepestTextOrLineBreak,
  getRightDeepestTextOrLineBreak,
  isRichTextNonFormatTypographic,
  isRichTextTopLevelTypographic,
} from './ContentEditorTypeUtils';
import {
  ADD_BLOCK_QUOTE,
  ADD_UL,
  ADD_OL,
  ADD_MEDIA,
  ADD_HTML_EMBED,
  ADD_IMAGE,
  ADD_CODE_BLOCK,
  ADD_H1,
  ADD_H2,
  ADD_H3,
  ADD_H4,
  ADD_H5,
  ADD_H6,
  ADD_PARAGRAPH,
  ADD_AI_PARAGRAPH,
  ADD_COMPONENT,
  ATTR_ALT_PATH,
  CONTENT_EDITOR_MENUS,
  FIGCAPTION_PLACEHOLDER_TEXT,
  FORMAT_BUTTON,
  FORMAT_BUTTONS,
  DEFAULT_FIGURE_ALIGNMENT,
} from './constants';

/**
 * A utility function to be used in a pipe with ContentEditorActiveAndNextId.
 * Wrap functions that modify the state but do not require access to the id in this to ensure the id is passed to subsequent functions in the pipe.
 *
 *
 * Example usage:
 * const [resultingState, nextId] = pipe([
 *   idPassthrough(setActiveToolbar(activeToolbar)),
 *    // someFunction accepts ContentEditorActiveAndNextId and returns ContentEditorActiveAndNextId with a new id. Usually because it has created with the previously provided id.
 *    someFunction,
 *   idPassthrough(setSelection(newSelection)),
 *   // ... other operations
 * ])([state, action.payload.initialId)]);
 */
export const idPassthrough =
  <T>(fn: (arg1: T) => T) =>
  ([state, id]: [T, string]): [T, string] => {
    return [fn(state), id];
  };

const selectNewTypo =
  // move caret to string element at the start new element
  (elementId: string) => (_state: ContentEditorActive) => {
    const subtree = _state.subtree;
    const deepStartRangeSelection =
      moveCaretToDeepStartOfNode(elementId)(subtree);
    return deepStartRangeSelection
      ? _state.set('selection', deepStartRangeSelection)
      : _state;
  };

const selectNewNode = (elementId: string) => (_state: ContentEditorActive) => {
  return selectNode(elementId)(_state);
};

// move caret to string element at the start new element or selects node
export const selectNewElement = {
  [ADD_BLOCK_QUOTE]: selectNewTypo,
  [ADD_UL]: selectNewTypo,
  [ADD_OL]: selectNewTypo,
  [ADD_MEDIA]: selectNewNode,
  [ADD_HTML_EMBED]: selectNewNode,
  [ADD_IMAGE]: selectNewNode,
  [ADD_CODE_BLOCK]: selectNewNode,
  [ADD_H1]: selectNewTypo,
  [ADD_H2]: selectNewTypo,
  [ADD_H3]: selectNewTypo,
  [ADD_H4]: selectNewTypo,
  [ADD_H5]: selectNewTypo,
  [ADD_H6]: selectNewTypo,
  [ADD_PARAGRAPH]: selectNewTypo,
  [ADD_AI_PARAGRAPH]: selectNewTypo,
  [ADD_COMPONENT]: selectNewNode,
};

export const setChildren =
  (children: ExpressionElement[], path: ExpressionPath = []) =>
  (element: ExpressionElement): ExpressionElement => {
    const elementExpression = Expressions.updateIn(
      EElement(element),
      [...path, {in: 'Element'}, {in: 'Record', at: 'children'}],
      () => EList(children.map(EElement))
    );

    return Expressions.getElement(elementExpression) || element;
  };

export const updateChildren =
  (
    elementId: string,
    fn: (children: Array<ExpressionElement>) => Array<ExpressionElement>
  ) =>
  (root: ExpressionElement): ExpressionElement => {
    const result = Expressions.updateElement(
      elementId,
      (el) => {
        const children = getChildren(el);
        if (!children) return el;
        const newChildren = fn(children);
        return newChildren === children ? el : setChildren(fn(children))(el);
      },
      EElement(root)
    );
    return Expressions.getElement(result) || root;
  };

const setElementId =
  (id: string) =>
  (element: ExpressionElement): ExpressionElement => ({
    ...element,
    id,
  });

export const getTextValue = (element: ExpressionElement): string => {
  const val = Expressions.getText(element.data) || '';
  if (val.trim() === ZWJ_RAW) {
    return '';
  }
  return val.trim();
};

export const setStringValue =
  (id: string, value: string) =>
  (root: ExpressionElement): ExpressionElement => {
    return (
      Expressions.getElement(
        Expressions.updateElement(
          id,
          updateData((_) => EText(value)),
          EElement(root)
        )
      ) ?? root
    );
  };

export const collectText = Expressions.everything(
  (a: string) => (b: string) => a + b
);
export const getAllTextInElement = (root: ExpressionElement): string => {
  return collectText(
    matchExpression({
      Element: (ele) => {
        return Expressions.resume(getTextValue(ele));
      },
      _: () => Expressions.resume(''),
    })
  )(EElement(root));
};

export const isEmptyTypoOrLineBreak = (element: ExpressionElement): boolean => {
  return (
    getAllTextInElement(element) === '' &&
    findElementDescendant(element, isLineBreakElement) === undefined
  );
};

export const findFurthestFormatElement = (
  root: Expression,
  elId: string,
  pred: (candidate: ExpressionElement) => boolean
) =>
  getElementWithAncestors(root)(elId).find(
    (candidate) => isFormatElement(candidate) && pred(candidate)
  );

export const updateSubtree =
  (updater: (arg1: ExpressionElement) => ExpressionElement) =>
  (state: ContentEditorActive) =>
    state.update('subtree', updater);

export const setSubtree =
  (expressionElement: ExpressionElement) => (state: ContentEditorActive) =>
    state.set('subtree', expressionElement);

export const setShouldSyncToComponentMap =
  (shouldSync: boolean) => (state: ContentEditorActive) =>
    state.set('shouldSyncToComponentMap', shouldSync);

export const setRemovedComponentIds =
  (ids: string[]) => (state: ContentEditorActive) =>
    state.set('removedComponentIds', ids);

export const setAddedComponentIds =
  (ids: string[]) => (state: ContentEditorActive) =>
    state.set('addedComponentIds', ids);

export const setComponentChangeSource =
  (source: RichTextComponentSource) => (state: ContentEditorActive) =>
    state.set('componentChangeSource', source);

export const setShouldCloseAfterComponentAdd =
  (shouldClose: boolean) => (state: ContentEditorActive) =>
    state.set('shouldCloseAfterComponentAdd', shouldClose);

export const setAiData =
  (aiData: Partial<ContentEditorActive['aiData']>) =>
  (state: ContentEditorActive) =>
    state.set('aiData', {
      ...state.aiData,
      ...aiData,
    });

export const updateElementById =
  (id: string, fn: (el: ExpressionElement) => ExpressionElement) =>
  (root: ExpressionElement) =>
    Expressions.getElement(Expressions.updateElement(id, fn, EElement(root))) ||
    root;

export const insertElement =
  (
    element: ExpressionElement,
    anchor: string,
    position: RelativePositionPlacementOptions
  ) =>
  (root: ExpressionElement) =>
    Expressions.getElement(
      addElementAtAnchorById(EElement(root), element, anchor, position)
    ) ?? root;

export const replaceElement = (id: string, newElement: ExpressionElement) =>
  updateData((data) => replaceElementById(data, id, EElement(newElement)));

export const updateParentChildren =
  (
    elId: string,
    fn: (offspring: Array<ExpressionElement>) => Array<ExpressionElement>
  ) =>
  (subtree: ExpressionElement) => {
    const parent = Expressions.getParent(EElement(subtree), elId);
    if (!parent) return subtree;
    return updateChildren(parent.id, fn)(subtree);
  };

export const removeAddMenuFocus = (state: ContentEditorActive) => {
  const currentMenuFocus = state.slashCommand.isMenuFocused;

  if (!currentMenuFocus) return state;
  if (currentMenuFocus && state.activeToolbar === CONTENT_EDITOR_MENUS.ADD_MENU)
    return state;

  return state.set('slashCommand', {
    ...state.slashCommand,
    isMenuFocused: false,
  });
};

// Not a UI selector:
const selectEndOfNewParagraph =
  (paragraphId: string) => (_state: ContentEditorActive) => {
    return _state.set(
      'selection',
      moveCaretToDeepStartOfNode(paragraphId)(_state.subtree) ||
        _state.selection
    );
  };

const moveCaretToTargetElement =
  (
    // direction is required to so the caret is at the side closest to where it arrives from: end <--- left | right ---> start
    direction: 'left' | 'right',
    targetElement: ExpressionElement
  ) =>
  (state: ContentEditorActive) => {
    const {subtree} = state;

    // When moving left, if the targetElement has a figcaption, we want a Range selection with the caret at the end
    // of the figcaption, otherwise if the targetSibling is a figure we want a figure selection.
    const moveIntoFigcaption = Boolean(
      direction === 'left' &&
      isFigure(targetElement) &&
      findElementDescendant(targetElement, isFigCaption)
    );

    if (
      !moveIntoFigcaption &&
      (isCodeBlock(targetElement) ||
        isEmbed(targetElement) ||
        isFigure(targetElement) ||
        isUserComponent(targetElement.type))
    ) {
      return state.set(
        'selection',
        reifySelectionRecord({type: 'Node', nodeId: targetElement.id})
      );
    } else {
      const updatedSelection =
        direction === 'left'
          ? moveCaretToDeepEndOfNode(Expressions.getElementId(targetElement))(
              subtree
            )
          : moveCaretToDeepStartOfNode(Expressions.getElementId(targetElement))(
              subtree
            );

      return updatedSelection ? setSelection(updatedSelection)(state) : state;
    }
  };

// finds the top level element that will move into before diving into nested (strong, emphasized, ficaptions etc)
export const getTargetElement =
  (direction: 'left' | 'right') =>
  (state: ContentEditorActive): ExpressionElement | null => {
    const {subtree, selection} = state;
    const selectedNodeId =
      direction === 'left'
        ? getNodeIdFromSelectionAtLeft(selection)
        : getNodeIdFromSelectionAtRight(selection);

    if (selectedNodeId === null) {
      return null;
    }

    const closestTopLevelElement =
      getClosestTopLevelElement(selectedNodeId, subtree) ??
      (() => {
        const selectedElement = Expressions.getElementById(
          EElement(subtree),
          selectedNodeId
        );
        return selectedElement &&
          isRichTextTopLevelElement(subtree)(selectedElement)
          ? selectedElement
          : null;
      })();

    if (!closestTopLevelElement) {
      return null;
    }

    return direction === 'left'
      ? findLeftMoveTarget({subtree, closestTopLevelElement})
      : findRightMoveTarget({subtree, closestTopLevelElement});
  };

// moves to deepest element in sibling top level element. For example:
// <p>Hello <b>World</b></p><p> | Goodbye World</p>
// The caret is at the start of the second paragraph (a top level element). If moved left it would be:
// <p>Hello <b>World | </b></p><p>Goodbye World</p>
//  It has moved from the text node at the start of second paragraph, and moved to the text node within strong element at the end of the first paragraph
export const moveToDeepSibling =
  (direction: 'left' | 'right') =>
  (state: ContentEditorActive): ContentEditorActive => {
    const targetElement = getTargetElement(direction)(state);

    if (targetElement === null) {
      return state;
    }

    return moveCaretToTargetElement(direction, targetElement)(state);
  };

// if possible moves to deep left sibling, if not, it creates a paragraph
export const moveToDeepLeftSiblingOrCreateParagraph = (
  stateAndNextId: ContentEditorActiveAndNextId
) => {
  const [state] = stateAndNextId;
  const targetElement = getTargetElement('left')(state);
  return moveToTargetOrAddParagraph({
    targetElement,
    moveCaretDirection: 'left',
  })(stateAndNextId);
};

// 1) attempts to find next top level element in preferredDirection
// 2) if unable, will find next top level element in opposite direction
export const findMoveTarget = ({
  subtree,
  closestTopLevelElement,
  preferredDirection,
}: {
  subtree: ExpressionElement;
  closestTopLevelElement: ExpressionElement;
  preferredDirection: 'left' | 'right';
}): {
  targetElement: ExpressionElement | null;
  moveCaretDirection: 'left' | 'right' | null;
} => {
  const parentElement = Expressions.getParent(
    EElement(subtree),
    closestTopLevelElement.id
  );

  if (!parentElement) {
    return {targetElement: null, moveCaretDirection: null};
  }

  const childrenListElements = getChildren(parentElement);

  if (!childrenListElements) {
    return {targetElement: null, moveCaretDirection: null};
  }

  const topLevelIndex = childrenListElements.findIndex((childElement) => {
    return Expressions.getElementId(childElement) === closestTopLevelElement.id;
  });

  const moveCaretDirection: 'left' | 'right' | null = (() => {
    const canMoveLeft = topLevelIndex > 0;
    const canMoveRight = topLevelIndex < childrenListElements.length - 1;

    if (preferredDirection === 'left') {
      return canMoveLeft ? 'left' : canMoveRight ? 'right' : null;
    } else {
      return canMoveRight ? 'right' : canMoveLeft ? 'left' : null;
    }
  })();

  const targetElement =
    moveCaretDirection === null
      ? null
      : moveCaretDirection === 'left'
        ? childrenListElements[topLevelIndex - 1]
        : childrenListElements[topLevelIndex + 1];

  return {targetElement: targetElement ?? null, moveCaretDirection};
};

const findLeftMoveTarget = ({
  subtree,
  closestTopLevelElement,
}: {
  subtree: ExpressionElement;
  closestTopLevelElement: ExpressionElement;
}): ExpressionElement | null => {
  const childrenListElements = getChildren(subtree);

  if (!childrenListElements) {
    return null;
  }

  const topLevelIndex = childrenListElements.findIndex((childElement) => {
    return Expressions.getElementId(childElement) === closestTopLevelElement.id;
  });

  return topLevelIndex > 0
    ? (childrenListElements[topLevelIndex - 1] ?? null)
    : null;
};

const findRightMoveTarget = ({
  subtree,
  closestTopLevelElement,
}: {
  subtree: ExpressionElement;
  closestTopLevelElement: ExpressionElement;
}): ExpressionElement | null => {
  const childrenListElements = getChildren(subtree);

  if (!childrenListElements) {
    return null;
  }

  const topLevelIndex = childrenListElements.findIndex((childElement) => {
    return Expressions.getElementId(childElement) === closestTopLevelElement.id;
  });

  return topLevelIndex < childrenListElements.length - 1
    ? (childrenListElements[topLevelIndex + 1] ?? null)
    : null;
};

// attempts to move but if is at edge of rich text (start or end) it creates an empty paragraph
export const moveToTargetOrAddParagraph =
  ({
    targetElement,
    moveCaretDirection,
  }: {
    targetElement: ExpressionElement | null;
    moveCaretDirection: 'left' | 'right' | null;
  }) =>
  ([
    state,
    nextId,
  ]: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    if (targetElement && moveCaretDirection) {
      return [
        moveCaretToTargetElement(moveCaretDirection, targetElement)(state),
        nextId,
      ];
    }

    // if there was nothing before or after the removed element so we want an empty rich text with an empty paragraph
    const [idState, emptyElement] = createParagraphWithZwj(nextId);

    const updatedState = setSubtree(
      Expressions.getElement(
        addElementAtAnchor(
          EElement(state.subtree),
          emptyElement,
          state.subtree,
          moveCaretDirection === 'left' ? 'prepend' : 'append'
        )
      ) || state.subtree
    )(state);

    const emptyParagraphSelection =
      moveCaretToDeepStartOfNode(updatedState.subtree.id)(
        updatedState.subtree
      ) || state.selection;
    return [setSelection(emptyParagraphSelection)(updatedState), idState.id];
  };

// this is for a very specific use case of handling empty list items when there are other list items.
export const removeEmptyListItemAndMoveCaret =
  ({
    preferredDirection,
    listItemId,
  }: {
    preferredDirection: 'left' | 'right';
    listItemId: string;
  }) =>
  (state: ContentEditorActive) => {
    const {subtree} = state;
    const listItem = Expressions.getElementById(EElement(subtree), listItemId);

    if (!listItem) {
      return state;
    }

    const {targetElement, moveCaretDirection} = findMoveTarget({
      subtree,
      closestTopLevelElement: listItem,
      preferredDirection,
    });

    return pipe<ContentEditorActive>([
      updateSubtree((_subtree) => {
        const updatedSubtreeExpr = removeElementById(
          EElement(_subtree),
          listItemId
        );
        const updatedSubtree = Expressions.getElement(updatedSubtreeExpr);
        return updatedSubtree || _subtree;
      }),
      (_state: ContentEditorActive) => {
        if (targetElement && moveCaretDirection) {
          return moveCaretToTargetElement(
            moveCaretDirection,
            targetElement
          )(_state);
        }

        // no fallback if no list item target as this is only used when we know there are other list items
        return _state;
      },
    ])(state);
  };

export const outdentListItem = (
  stateAndId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const {subtree, selection} = getFirst(stateAndId);
  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (rangeSelection === null) return stateAndId;

  const {anchorNodeId} = rangeSelection;
  const listItem = getClosestListItemElement(EElement(subtree), anchorNodeId);
  if (listItem === null) return stateAndId;
  return breakListItemOutOfList(Expressions.getElementId(listItem))(stateAndId);
};

export const handleBackspaceAtStartOfListItem = (
  stateAndId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const {subtree, selection} = getFirst(stateAndId);
  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (rangeSelection === null) return stateAndId;

  const {anchorNodeId} = rangeSelection;
  const listItem = getClosestListItemElement(EElement(subtree), anchorNodeId);
  if (listItem === null) return stateAndId;

  if (isEmptyTypoOrLineBreak(listItem))
    return handleBackspaceKeyInEmptyListItem(
      Expressions.getElementId(listItem)
    )(stateAndId);

  if (caretIsAtTheStartOfTheNode(Expressions.getElementId(listItem)))
    return breakListItemOutOfList(Expressions.getElementId(listItem))(
      stateAndId
    );

  return stateAndId;
};

export const handleBackspaceAtStartOfFigcaption = (
  state: ContentEditorActive
) => {
  const {subtree, selection} = state;
  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (!rangeSelection) return state;
  const figcaptionNode = Expressions.closestElement(
    subtree.data,
    rangeSelection.anchorNodeId,
    isFigCaption
  );

  if (figcaptionNode && caretIsAtTheStartOfTheNode(figcaptionNode.id)(state)) {
    const figureNode = Expressions.getParent(subtree.data, figcaptionNode.id);

    // TODO (Yoav): Later on use a util that selects the figure and toggles the figcaption
    // (in case it was empty)
    return figureNode ? selectNode(figureNode.id)(state) : state;
  }
  return state;
};

export const addParagraphWithEmptyString =
  (anchorId: string, position: RelativePositionPlacementOptions) =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const [state, paragraphId] = stateAndId;
    const [{id}, paragraphElement] = createParagraphWithZwj(paragraphId);

    return pair(
      pipe([
        updateSubtree((root) =>
          contentEditorGetElement(
            addElementAtAnchorById(
              EElement(root),
              paragraphElement,
              anchorId,
              position
            )
          )
        ),
        selectEndOfNewParagraph(paragraphId),
      ])(state),
      id
    );
  };

export const removeElement = (id: string) => (state: ContentEditorActive) => {
  return updateSubtree(updateData((data) => removeElementById(data, id)))(
    state
  );
};

const handleBackspaceKeyInEmptyListItem =
  (listItemId: string) =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const {subtree} = getFirst(stateAndId);
    const list = Expressions.getParent(EElement(subtree), listItemId);
    if (list === null) return stateAndId;
    const listChildren = getChildren(list) || [];
    // remove whole list if there is just one empty list item
    const listToRemove = listChildren.length === 1 ? list : null;
    // else remove only the list item
    const listItemToRemove =
      listChildren.length > 1
        ? Expressions.getElementById(EElement(subtree), listItemId)
        : null;

    const elementToRemove = listToRemove || listItemToRemove;
    if (elementToRemove === null) {
      return stateAndId;
    }

    const elementToRemoveParent = Expressions.getParent(
      EElement(subtree),
      Expressions.getElementId(elementToRemove)
    );
    if (elementToRemoveParent === null) return stateAndId;

    if (listToRemove) {
      const isInNestedList = isListItem(elementToRemoveParent);
      if (isInNestedList) {
        // Not a UI selector:
        // eslint-disable-next-line webflow/selector-memoization
        const selectEndOfParentListItem = (_state: ContentEditorActive) => {
          const selection = moveCaretToDeepEndOfNode(
            Expressions.getElementId(elementToRemoveParent)
          )(_state.subtree);
          if (!selection) return _state;
          return _state.set('selection', selection);
        };

        return idPassthrough(
          pipe([removeElement(listToRemove.id), selectEndOfParentListItem])
        )(stateAndId);
      } else {
        return pipe([
          addParagraphWithEmptyString(listToRemove.id, 'after'),
          idPassthrough(removeElement(listToRemove.id)),
        ])(stateAndId);
      }
    } else if (listItemToRemove) {
      return idPassthrough(
        removeEmptyListItemAndMoveCaret({
          preferredDirection: 'left',
          listItemId: listItemToRemove.id,
        })
      )(stateAndId);
    }
    return stateAndId;
  };

const cloneListNewChildren =
  (
    listChildren: ExpressionElement[],
    list: ExpressionElement,
    newListId: string,
    anchorNodeId: string,
    position: RelativePositionPlacementOptions
  ) =>
  (subtree: ExpressionElement): ExpressionElement => {
    const newList = pipe([setChildren(listChildren), setElementId(newListId)])(
      list
    );

    const updatedSubtree = addElementAtAnchorById(
      EElement(subtree),
      newList,
      anchorNodeId,
      position
    );
    return updatedSubtree
      ? Expressions.getElement(updatedSubtree) || subtree
      : subtree;
  };

const insertNewListItem =
  ({
    listItem,
    anchorElement,
    id,
  }: {
    listItem: ExpressionElement;
    anchorElement: ExpressionElement;
    id: string;
  }) =>
  (_root: ExpressionElement): ExpressionElement => {
    const newListItem = getSecond(createListItem({id}));

    const listItemChildren = listItem ? getChildren(listItem) || [] : [];

    const newListItemWithChildren = setChildren(listItemChildren)(newListItem);

    const updatedExpression = contentEditorGetElement(
      addElementAtAnchor(
        EElement(_root),
        newListItemWithChildren,
        anchorElement,
        'after'
      )
    );

    return updatedExpression;
  };

const createListBefore =
  (
    listChildren: ExpressionElement[],
    list: ExpressionElement,
    itemIndex: number
  ) =>
  (expressionElement: ExpressionElement): ExpressionElement => {
    const leftSideItems = listChildren.slice(0, itemIndex);
    // no list items to wrap
    if (leftSideItems.length === 0) {
      const listId = Expressions.getElementId(list);
      const rootWithRemovedList = removeElementById(
        EElement(expressionElement),
        listId
      );
      return rootWithRemovedList
        ? Expressions.getElement(rootWithRemovedList) || expressionElement
        : expressionElement;
    }

    return updateChildren(
      Expressions.getElementId(list),
      (_) => leftSideItems
    )(expressionElement);
  };

const createListAfter =
  (
    listChildren: ExpressionElement[],
    list: ExpressionElement,
    newListId: string,
    itemIndex: number,
    anchorNodeId: string,
    position: RelativePositionPlacementOptions
  ) =>
  (subtree: ExpressionElement): ExpressionElement => {
    const rightSideItems = listChildren.slice(itemIndex + 1);
    // no list items to wrap
    if (rightSideItems.length === 0) return subtree;
    return cloneListNewChildren(
      rightSideItems,
      list,
      newListId,
      anchorNodeId,
      position
    )(subtree);
  };

// after a new element is created, this function splits the list around it or deletes the list if empty.
export const handleList =
  (
    newElementId: string,
    listItemId: string,
    listId: string,
    newListId: string
  ) =>
  (subtree: ExpressionElement): ExpressionElement => {
    const subtreeExpr = EElement(subtree);
    const newElement = Expressions.getElementById(subtreeExpr, newElementId);
    if (newElement === null) return subtree;
    const listItem = Expressions.getElementById(subtreeExpr, listItemId);
    const list = Expressions.getElementById(subtreeExpr, listId);
    if (list === null) return subtree;
    const itemIndex = Expressions.elementIndex(listItemId)(
      list ? EElement(list) : subtreeExpr
    );
    if (itemIndex === null) return subtree;
    const listChildren = list ? getChildren(list) || [] : [];
    const breakingOutOnlyItem = listChildren.length === 1;

    if (breakingOutOnlyItem) {
      const rootWithRemovedElement = removeElementById(subtreeExpr, listId);
      if (rootWithRemovedElement === null) return subtree;
      return Expressions.getElement(rootWithRemovedElement) || subtree;
    } else if (listItem) {
      // recreate lists
      return pipe([
        // modify existing list to only contain previous-siblings of the original list item
        createListBefore(listChildren, list, itemIndex),
        // add the siblings of after-siblings of the original list item to be a new list within the new list item
        createListAfter(
          listChildren,
          list,
          newListId,
          itemIndex,
          newElementId,
          // with a list item, we append the list to it as a nested list, with a paragraph, we place the list after it.
          isParagraph(newElement) ? 'after' : 'append'
        ),
      ])(subtree);
    } else {
      return subtree;
    }
  };

const moveNestedList =
  (
    element: ExpressionElement,
    children: ExpressionElement[],
    anchorId: string,
    listItemId: string,
    itemParentChildren: ExpressionElement[]
  ) =>
  (root: ExpressionElement): ExpressionElement => {
    const newChildren = itemParentChildren.reduce<ExpressionElement[]>(
      (acc, ele) => {
        if (Expressions.getElementId(ele) === listItemId) {
          acc.push(
            Expressions.getElement(
              removeElementById(EElement(ele), element.id)
            ) || ele
          );
          acc.push(...children);
        } else {
          acc.push(ele);
        }
        return acc;
      },
      []
    );
    return updateChildren(anchorId, (_) => newChildren)(root);
  };

const nestedListItemsToParentList =
  (
    nestedList: ExpressionElement | undefined,
    listItemId: string,
    parentList: ExpressionElement
  ) =>
  (subtree: ExpressionElement) => {
    if (nestedList === undefined) return subtree;
    const children = getChildren(nestedList) || [];
    const parentListChildren = getChildren(parentList) || [];
    if (parentListChildren.length === 0) return subtree;
    const parentListId = Expressions.getElementId(parentList);

    return moveNestedList(
      nestedList,
      children,
      parentListId,
      listItemId,
      parentListChildren
    )(subtree);
  };

export const splitList =
  (list: ExpressionElement, listItemId: string) =>
  (subtreeAndId: ElementAndId): ElementAndId => {
    const props = Expressions.getRecord(list.data);
    if (!props || !props.tag) return subtreeAndId;
    const listType = Expressions.getEnum(props.tag) || 'ul';
    if (listType !== 'ul' && listType !== 'ol') return subtreeAndId;
    const listChildren = getChildren(list) || [];
    const listItemIndex = listChildren.findIndex(
      (child) => child.id === listItemId
    );
    const leftList = setChildren(listChildren.slice(0, listItemIndex))(list);

    const rightListId = getSecond(subtreeAndId);
    const rightList = {
      id: rightListId,
      type: qualify(Plugins.name('Basic'), Component.name('List')),
      data: ERecord({
        tag: EEnum(listType),
        list: ERecord({
          type: EText('list'),
        }),
        children: EList(listChildren.slice(listItemIndex + 1).map(EElement)),
      }),
    };

    const subtree = getFirst(subtreeAndId);
    return pair(
      pipe([
        updateData((data) =>
          addElementAtAnchorById(data, rightList, list.id, 'after')
        ),
        replaceElement(list.id, leftList),
      ])(subtree),
      incrementGuid(rightListId)
    );
  };

const insertParagraph =
  (listItemId: string, itemIndex: number, anchorElement: ExpressionElement) =>
  (rootAndId: ElementAndId): ElementAndId => {
    const root = getFirst(rootAndId);
    const newParagraphId = getSecond(rootAndId);
    const listItem =
      Expressions.getElementById(EElement(root), listItemId) || null;
    if (listItem === null) return rootAndId;

    const listItemChildren = getChildren(listItem) || [];

    const [{id: nextId}, newParagraph] = createParagraph({
      id: newParagraphId,
      children: EList(listItemChildren.map(EElement)),
    });

    const position = itemIndex === 0 ? 'before' : 'after';

    return pair(
      insertElement(newParagraph, anchorElement.id, position)(root),
      nextId
    );
  };

export const changeListType =
  (type: 'ul' | 'ol') => (state: ContentEditorActive) => {
    const {subtree, selection} = state;

    const rangeSelection = assertRangeSelectionRecordType(selection);
    if (rangeSelection === null) return state;

    const listElement = Expressions.closestElement(
      subtree.data,
      rangeSelection.anchorNodeId,
      isList
    );
    if (!listElement) return state;

    return updateSubtree(
      updateData((root) =>
        Expressions.updateElement(
          listElement.id,
          updateData((data) => {
            const props = Expressions.getRecord(data);
            if (!props) return data;
            const {tag} = props;
            if (tag && Expressions.getEnum(tag) === type) return data;
            return ERecord({...props, tag: EEnum(type)});
          }),
          root
        )
      )
    )(state);
  };

const breakListItemOutOfList =
  (listItemId: string) =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const state = getFirst(stateAndId);
    const rootElement = EElement(state.subtree);
    const listItem = Expressions.getElementById(rootElement, listItemId);
    if (listItem === null) return stateAndId;

    const listNode = Expressions.getParent(rootElement, listItemId);
    if (listNode === null) return stateAndId;
    const listId = Expressions.getElementId(listNode);

    // parent of list node (grandparent of listItem)
    const parentNode = Expressions.getParent(rootElement, listId);

    if (parentNode === null) return stateAndId;

    // current listItem is inside another list item, therefore it is within a nested list
    const parentListItem = isListItem(parentNode) ? parentNode : null;

    const parentListItemChildren = parentListItem
      ? getChildren(parentListItem) || []
      : [];

    const listItemChildren = listItem ? getChildren(listItem) || [] : [];

    const nestedList = listItemChildren.find(isList);

    const itemIndex = parentListItemChildren.indexOf(listItem);

    if (parentListItem) {
      const newListItemId = getSecond(stateAndId);
      const newListId = incrementGuid(newListItemId);

      return pair(
        pipe([
          updateSubtree(
            insertNewListItem({
              listItem,
              anchorElement: parentListItem,
              id: newListItemId,
            })
          ),
          updateSubtree(
            handleList(newListItemId, listItemId, listId, newListId)
          ),
          (_state: ContentEditorActive) => {
            return _state.set(
              'selection',
              moveCaretToDeepStartOfNode(newListItemId)(_state.subtree) ||
                _state.selection
            );
          },
        ])(state),
        incrementGuid(newListId)
      );
    } else {
      return pipe<ContentEditorActiveAndNextId>([
        idPassthrough(
          updateSubtree(
            nestedListItemsToParentList(nestedList, listItemId, listNode)
          )
        ),
        ([_state, newParagraphId]) => {
          const [subtree, newListId] = insertParagraph(
            listItemId,
            itemIndex,
            listNode
          )(pair(_state.subtree, newParagraphId));
          return pair(
            pipe([
              setSubtree(
                handleList(
                  newParagraphId,
                  listItemId,
                  listId,
                  newListId
                )(subtree)
              ),
              (s: ContentEditorActive) => {
                const selection = moveCaretToDeepStartOfNode(newParagraphId)(
                  s.subtree
                );
                if (!selection) return s;
                return s.set('selection', selection);
              },
            ])(_state),
            incrementGuid(newListId)
          );
        },
      ])(stateAndId);
    }
  };

const splitTextNodeAndProcessHalf = (
  splitOffset: number,
  sideToProcess: 'left' | 'right',
  elementToSplit: ExpressionElement,
  nextId: string,
  state: ContentEditorActive,
  trackedElements: Array<ExpressionElement>
) => {
  const {subtree, selection} = state;

  const processLeftSide = sideToProcess === 'left';
  const currentTextValue = Expressions.getText(elementToSplit.data) ?? '';
  const sideToKeep = processLeftSide ? 'right' : 'left';
  const newPosition = processLeftSide ? 'before' : 'after';

  const newValues = {
    left: currentTextValue.slice(0, splitOffset),
    right: currentTextValue.slice(splitOffset),
  } as const;

  const textToProcess = newValues[sideToProcess];

  // Don't wrap empty values
  if (!textToProcess.length) {
    return {state, trackedElements, nextId};
  }

  const [newNodeToProcess] = createStringElement(textToProcess, nextId);

  const newOffset = processLeftSide ? newValues[sideToProcess].length : 0;

  const updatedSubtree = pipe([
    insertElement(newNodeToProcess, elementToSplit.id, newPosition),
    setStringValue(elementToSplit.id, newValues[sideToKeep]),
  ])(subtree);

  const updatedSelection = (() => {
    const rangeSelection = assertRangeSelectionRecordType(selection);

    if (rangeSelection === null) return selection;

    const withinSingleTextNode =
      rangeSelection.anchorNodeId === rangeSelection.focusNodeId;

    if (withinSingleTextNode)
      return selectText(newNodeToProcess.id, textToProcess)(rangeSelection);

    if (processLeftSide)
      return setRightEndOfSelection(
        newNodeToProcess.id,
        newOffset
      )(rangeSelection);

    return setLeftEndOfSelection(
      newNodeToProcess.id,
      newOffset
    )(rangeSelection);
  })();

  return {
    state: state
      .set('subtree', updatedSubtree)
      .set('selection', updatedSelection),
    trackedElements: append(trackedElements, newNodeToProcess),
    nextId: incrementGuid(nextId),
  };
};

const splitTextNodeOnBothSidesAndProcessMiddle = (
  leftOffset: number,
  rightOffset: number,
  element: ExpressionElement,
  nextId: string,
  state: ContentEditorActive,
  trackedElements: Array<ExpressionElement>
) => {
  if (leftOffset === rightOffset) {
    return {state, trackedElements, nextId};
  }
  const {subtree, selection} = state;
  const currentTextValue = Expressions.getText(element.data) ?? '';
  const newLeftSideValue = currentTextValue.slice(0, leftOffset);
  const newMiddleValue = currentTextValue.slice(leftOffset, rightOffset);
  const newRightSideValue = currentTextValue.slice(rightOffset);

  const middleElementId = nextId;
  const [textNodeToProcess] = createStringElement(
    newMiddleValue,
    middleElementId
  );

  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (rangeSelection === null)
    return {
      state,
      trackedElements: append(trackedElements, textNodeToProcess),
      nextId: incrementGuid(middleElementId),
    };

  const rightSideId = incrementGuid(middleElementId);
  const [newRightSideNode] = createStringElement(
    newRightSideValue,
    rightSideId
  );

  const updatedSubtree = pipe([
    insertElement(newRightSideNode, element.id, 'after'),
    insertElement(textNodeToProcess, element.id, 'after'),
    setStringValue(element.id, newLeftSideValue),
  ])(subtree);

  // If wrapping the left side of the split, then we want the offset to be the end of the textNode
  // and set the right side of the selection.
  const updatedSelection = selectText(
    textNodeToProcess.id,
    newMiddleValue
  )(rangeSelection);

  return {
    state: state
      .set('subtree', updatedSubtree)
      .set('selection', updatedSelection),
    trackedElements: append(trackedElements, textNodeToProcess),
    nextId: incrementGuid(rightSideId),
  };
};

export const shouldUnwrapTextNode = (
  targetType: Qualified<Component.ComponentName>
) => {
  return (subtree: ExpressionElement, descendantId: string) =>
    Boolean(
      Expressions.closestElement(
        subtree.data,
        descendantId,
        elementTypeEquals(targetType)
      )
    );
};

export const shouldWrapTextNode = (
  targetType: Qualified<Component.ComponentName>
) => {
  return (subtree: ExpressionElement, descendantId: string) =>
    !Expressions.closestElement(
      subtree.data,
      descendantId,
      elementTypeEquals(targetType)
    );
};

export const splitAndTrackSelectedTextNodes = (
  state: ContentEditorActive,
  shouldTrackTextNode: (el: ExpressionElement, id: string) => boolean,
  seedId: string
) => {
  const {subtree} = state;
  const selection = assertRangeSelectionRecordType(state.selection);

  if (!selection) return {state, trackedElements: emptyArray, nextId: seedId};

  // Selection Details
  const {leftNodeId, leftOffset, rightNodeId, rightOffset} =
    getLeftRightPosition(selection);
  const selectedTextNodesAndLineBreaks = getSelectedTextAndLineBreakNodes(
    subtree,
    selection
  );
  const rightNode = Expressions.getElementById(subtree.data, rightNodeId);
  if (!rightNode) return {state, trackedElements: emptyArray, nextId: seedId};
  const rightNodeTextValue = Expressions.getText(rightNode.data);
  const atEdgeOfLeftNode = leftOffset === 0;
  const atEdgeOfRightNode =
    rightNodeTextValue && rightOffset === rightNodeTextValue.length;
  const withinBoundsOfSingleTextNode = leftNodeId === rightNodeId;
  return selectedTextNodesAndLineBreaks.reduce<{
    state: ContentEditorActive;
    trackedElements: Array<ExpressionElement>;
    nextId: string;
  }>(
    (acc, el) => {
      const {state: _state, trackedElements, nextId} = acc;
      // If the text node we want to wrap/split and wrap, is already a descendent of that format node
      // no need to wrap it.
      if (!shouldTrackTextNode(_state.subtree, el.id)) return acc;

      if (el.id === leftNodeId) {
        if (
          withinBoundsOfSingleTextNode &&
          atEdgeOfLeftNode &&
          atEdgeOfRightNode
        ) {
          return {
            state: _state,
            trackedElements: append(trackedElements, el),
            nextId,
          };
        } else if (
          withinBoundsOfSingleTextNode &&
          atEdgeOfLeftNode &&
          !atEdgeOfRightNode
        ) {
          // splice text node and add a new one on the left
          return splitTextNodeAndProcessHalf(
            rightOffset,
            'left',
            el,
            nextId,
            _state,
            trackedElements
          );
        } else if (
          withinBoundsOfSingleTextNode &&
          !atEdgeOfLeftNode &&
          atEdgeOfRightNode
        ) {
          // splice text node and add a new one on the right
          return splitTextNodeAndProcessHalf(
            leftOffset,
            'right',
            el,
            nextId,
            _state,
            trackedElements
          );
        } else if (
          withinBoundsOfSingleTextNode &&
          !atEdgeOfLeftNode &&
          !atEdgeOfRightNode
        ) {
          // selection in the middle of the text node, split text node, creating a new text node in between that's wrapped in format tag.
          return splitTextNodeOnBothSidesAndProcessMiddle(
            leftOffset,
            rightOffset,
            el,
            nextId,
            _state,
            trackedElements
          );
        } else if (atEdgeOfLeftNode) {
          // multi-node selection, leftNode is covered by selection
          return {
            state: _state,
            trackedElements: append(trackedElements, el),
            nextId,
          };
        } else {
          // multi-node selection, selection extends part-way into the left node, wrap the right side of it in format node
          return splitTextNodeAndProcessHalf(
            leftOffset,
            'right',
            el,
            nextId,
            _state,
            trackedElements
          );
        }
      } else if (el.id === rightNodeId) {
        if (atEdgeOfRightNode) {
          // multi-node selection, The whole node is selected, wrap it in a format node.
          return {
            state: _state,
            trackedElements: append(trackedElements, el),
            nextId,
          };
        } else {
          // multi-node selection that extends part-way into the right node, split the text node, and wrap the left one.
          return splitTextNodeAndProcessHalf(
            rightOffset,
            'left',
            el,
            nextId,
            _state,
            trackedElements
          );
        }
      } else {
        // A node in the middle of and full covered by the selection
        return {
          state: _state,
          trackedElements: append(trackedElements, el),
          nextId,
        };
      }
    },
    {state, trackedElements: emptyArray, nextId: seedId}
  );
};

export const raiseNodesOutOfFormatNode = (
  elements: Array<ExpressionElement>,
  elementType: Qualified<Component.ComponentName>,
  state: ContentEditorActive,
  nextId: string
) => elements.reduce(raiseNodeOutsideFormatNode(elementType), {state, nextId});

export const raiseNodeOneLevel = (
  absorbParent: (
    el: ExpressionElement,
    parent: ExpressionElement,
    nextId: string
  ) => {element: ExpressionElement; nextId: string},
  element: ExpressionElement,
  parent: ExpressionElement,
  subtree: ExpressionElement,
  nextId: string
) => {
  const middle = absorbParent(element, parent, nextId);

  const updatedSubtree = updateParentChildren(parent.id, (ancestors) => {
    const parentIndex = ancestors.findIndex((el) => el.id === parent.id);
    if (parentIndex < 0) return ancestors;
    const offspring = getChildren(parent) ?? [];
    const index = offspring.findIndex((el) => el.id === element.id);
    if (index < 0) return ancestors;
    const right =
      index < offspring.length - 1
        ? [
            setChildren(offspring.slice(index + 1))({
              id: middle.nextId,
              type: parent.type,
              data: parent.data,
            }),
          ]
        : emptyArray;
    const left =
      index === 0
        ? emptyArray
        : [setChildren(offspring.slice(0, index))(parent)];

    return [
      ...ancestors.slice(0, parentIndex),
      ...left,
      middle.element,
      ...right,
      ...ancestors.slice(parentIndex + 1),
    ];
  })(subtree);

  return {
    targetElement: middle.element,
    subtree: updatedSubtree,
    nextId: incrementGuid(middle.nextId),
  };
};

export const raiseNodeAbsorbingAncestors = (
  predicate: (parent: ExpressionElement) => boolean,
  absorbParent: (
    el: ExpressionElement,
    parent: ExpressionElement,
    nextId: string
  ) => {element: ExpressionElement; nextId: string},
  element: ExpressionElement,
  subtree: ExpressionElement,
  nextId: string
): {
  subtree: ExpressionElement;
  targetElement: ExpressionElement;
  nextId: string;
} => {
  const parent = Expressions.getParent(EElement(subtree), element.id);
  if (!parent) return {targetElement: element, subtree, nextId};

  if (namesEqual(element.type, parent.type))
    return {
      targetElement: element,
      subtree: raiseChildrenToParent(subtree, element.id),
      nextId,
    };

  const result = raiseNodeOneLevel(
    absorbParent,
    element,
    parent,
    subtree,
    nextId
  );

  if (predicate(parent)) return result;

  // Recurse until the predicate returns true
  return raiseNodeAbsorbingAncestors(
    predicate,
    absorbParent,
    result.targetElement,
    result.subtree,
    result.nextId
  );
};

// Determine if two sibling elements can be combined into one
export const shouldMerge = (a: ExpressionElement, b: ExpressionElement) => {
  const aIsString = isStringElement(a);
  const bIsString = isStringElement(b);

  if (aIsString && bIsString) return true;

  if (!aIsString && !bIsString) {
    if (!namesEqual(a.type, b.type)) return false;

    const aWithoutChildren = setChildren(emptyArray)(a);
    const bWithoutChildren = setChildren(emptyArray)(b);
    return isEqual(aWithoutChildren.data, bWithoutChildren.data);
  }

  return false;
};

const getMergePaths = (
  leftNode: ExpressionElement,
  rightNode: ExpressionElement,
  subtree: ExpressionElement
): null | {left: ExpressionPath; right: ExpressionPath} => {
  const leftChildren = getChildren(leftNode);
  const leftZipChild = leftChildren
    ? leftChildren[leftChildren.length - 1]
    : undefined;
  const rightChildren = getChildren(rightNode);
  const rightZipChild = rightChildren ? rightChildren[0] : undefined;
  if (leftZipChild && rightZipChild && shouldMerge(leftZipChild, rightZipChild))
    return getMergePaths(leftZipChild, rightZipChild, subtree);

  const left = getKeyPathToElement(subtree.data, leftNode.id);
  if (!left) return null;
  const right = getKeyPathToElement(subtree.data, rightNode.id);
  if (!right) return null;

  return {left, right};
};

const isSelectableElement = (el: ExpressionElement) =>
  isStringElement(el) || isLineBreakElement(el) || isFigure(el);

// This is meant to be used after deleting a node, when you want the caret to
// move and be fenced within a nonFormatTypo node. it's designed to navigate
// around line breaks but can't really handle the ux of figures. It differs in
// behavior from moveToDeepSibling.
// NOTE: In the future it may be merged with moveToDeepSibling if we ever need
// figures, widgets, or plugins to exist at arbitrary depths (instead of
// always direct children of the rich text element)
export const moveToNeighbor = (
  direction: 'left' | 'right',
  parent: ExpressionElement, // the parent of the node that was deleted
  fromIndex: number, // the index of the node that was deleted in it's parent.children
  neighborhood: ExpressionElement, // the nonFormatTypo node that fences in the caret during this operation
  state: ContentEditorActive
) => {
  const {subtree, selection} = state;
  const neighbors = flatten(neighborhood);
  const preferLeftDirection = direction === 'left';
  const siblings = getChildren(parent) || [];
  const isLastIndex = fromIndex === siblings.length;

  if (direction === 'right' && isLastIndex) {
    // If the caret is between to line breaks and then uses DELETE to remove one
    // then it'll be at the children.size index (after the now last line break)
    // and fromIndex will be out of bounds. If that's the case and we're moving
    // right, then there's nothing to do.
    return selection;
  }
  // Convert caret index to a child index, if it was the lastIndex there won't
  // be a child there
  const childIndex = isLastIndex ? fromIndex - 1 : fromIndex;
  const childAtIndex = siblings[childIndex];
  if (!childAtIndex) return selection;
  const childIndexId = childAtIndex.id;
  const neighborIndex = neighbors.findIndex((n) => n.id === childIndexId);
  // Note: If it was the lastIndex there was no child there (see converting the
  // caret index to child index above). But now that we have the neighbor index
  // we want to add that 1 position back to make sure to include that last child
  // in the search
  const searchFromIndex = isLastIndex ? neighborIndex + 1 : neighborIndex;
  const prevNeighbor = neighbors
    .slice(0, searchFromIndex)
    .reverse()
    .find(isSelectableElement);
  const nextNeightbor = neighbors
    .slice(neighborIndex)
    .find(isSelectableElement);

  const targetNeighbor = preferLeftDirection
    ? prevNeighbor || nextNeightbor
    : nextNeightbor || prevNeighbor;

  if (!targetNeighbor) return selection;

  if (isStringElement(targetNeighbor)) {
    const movingCaretLeft = targetNeighbor === prevNeighbor;
    // Can’t use moveCaretToDeepEndOfNode cause it trims whitespace when setting
    // the offset.
    if (movingCaretLeft) {
      const lastCharOffset = (Expressions.getText(targetNeighbor.data) || '')
        .length;
      return RangeSelectionRecord({
        anchorNodeId: targetNeighbor.id,
        focusNodeId: targetNeighbor.id,
        anchorOffset: lastCharOffset,
        focusOffset: lastCharOffset,
        leftToRight: true,
      });
    }

    return moveCaretToDeepStartOfNode(targetNeighbor.id)(subtree) || selection;
  }

  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (rangeSelection === null) return selection;

  const targetParent = Expressions.getParent(
    EElement(subtree),
    targetNeighbor.id
  );
  if (!targetParent) return selection;
  const targetSiblings = getChildren(targetParent) || [];
  const targetIndex = targetSiblings.findIndex(
    (el) => el.id === targetNeighbor.id
  );
  const targetCaretIndex =
    targetNeighbor === prevNeighbor ? targetIndex + 1 : targetIndex;
  // if the target is a line-break, move the caret next to it
  return collapseTextSelection(
    rangeSelection,
    targetParent.id,
    targetCaretIndex
  );
};

// Since we typically use this after deleting a node, we pass in the parentNodeId
// so that we can find
export const moveToFormatNodeSibling =
  (direction: 'left' | 'right', parentId: string, fromIndex: number) =>
  (state: ContentEditorActive) => {
    const parent = Expressions.getElementById(
      EElement(state.subtree),
      parentId
    );
    if (!parent) return state;

    const nonFormatTypoNode = Expressions.closestElement(
      EElement(state.subtree),
      parentId,
      isRichTextNonFormatTypographic
    );
    if (!nonFormatTypoNode) return state;

    return state.set(
      'selection',
      moveToNeighbor(direction, parent, fromIndex, nonFormatTypoNode, state)
    );
  };

// Note: This is used for collapsed carets and text selections where one or both
// edges can be between line breaks, and as we tranform a subtree, eg: deleting,
// wrapping, or unwrapping content, the selection edges can end up between a
// line break and a textNode. Whenever that happens we generally want to bump
// that selection into the textNode. Generally prefering the textNode on the
// 'inside' of the selection. ie: between the left/right edges.
export const bumpSelectionEdgesIntoNearbyTextNodes = (
  state: ContentEditorActive
) => {
  const {subtree} = state;
  let selection = assertRangeSelectionRecordType(state.selection);
  if (!selection) return state;

  const {leftNodeId, leftOffset, rightNodeId, rightOffset} =
    getLeftRightPosition(selection);

  const leftNode = Expressions.getElementById(subtree.data, leftNodeId);
  const rightNode = Expressions.getElementById(subtree.data, rightNodeId);

  if (leftNode && !isStringElement(leftNode)) {
    const leftChildren = getChildren(leftNode) || [];
    const leftEdgeLeftSibling =
      leftOffset > 0 ? leftChildren[leftOffset - 1] : undefined;
    const leftEdgeRightSibling =
      leftOffset < leftChildren.length ? leftChildren[leftOffset] : undefined;

    if (leftEdgeRightSibling && isStringElement(leftEdgeRightSibling)) {
      selection = setLeftEndOfSelection(leftEdgeRightSibling.id, 0)(selection);
    } else if (leftEdgeLeftSibling && isStringElement(leftEdgeLeftSibling)) {
      const leftEdgeLeftSiblingValue =
        Expressions.getText(leftEdgeLeftSibling.data) || '';
      selection = setLeftEndOfSelection(
        leftEdgeLeftSibling.id,
        leftEdgeLeftSiblingValue.length
      )(selection);
    }
  }

  if (rightNode && !isStringElement(rightNode)) {
    const rightChildren = getChildren(rightNode) || [];
    const rightEdgeLeftSibling =
      rightOffset > 0 ? rightChildren[rightOffset - 1] : undefined;
    const rightEdgeRightSibling =
      rightOffset < rightChildren.length
        ? rightChildren[leftOffset]
        : undefined;

    if (rightEdgeLeftSibling && isStringElement(rightEdgeLeftSibling)) {
      const rightEdgeLeftSiblingValue =
        Expressions.getText(rightEdgeLeftSibling.data) || '';
      selection = setRightEndOfSelection(
        rightEdgeLeftSibling.id,
        rightEdgeLeftSiblingValue.length
      )(selection);
    } else if (
      rightEdgeRightSibling &&
      isStringElement(rightEdgeRightSibling)
    ) {
      selection = setRightEndOfSelection(
        rightEdgeRightSibling.id,
        0
      )(selection);
    }
  }

  return state.set('selection', selection);
};

const deepJoin =
  (
    direction: 'left' | 'right',
    parentListId: undefined | string,
    paths: {left: ExpressionPath; right: ExpressionPath}
  ) =>
  (state: ContentEditorActive) => {
    const {subtree, selection} = state;
    // This exits when we're merging something like an h3 (top-level typo) into an li (always nested in an ol/ul)
    // so mergePaths will be different depths and one will be shorter than the other.
    if (!paths.left.length || !paths.right.length) return state;

    const leftSideExpr = Expressions.getIn(subtree.data, paths.left);
    const leftSideNode = leftSideExpr
      ? Expressions.getElement(leftSideExpr)
      : undefined;
    const rightSideExpr = Expressions.getIn(subtree.data, paths.right);
    const rightSideNode = rightSideExpr
      ? Expressions.getElement(rightSideExpr)
      : undefined;

    if (
      leftSideNode === rightSideNode ||
      !leftSideNode ||
      !rightSideNode ||
      isUnmergeableElement(leftSideNode) ||
      isUnmergeableElement(rightSideNode)
    )
      // It's joined now.
      return state;

    const recurse = deepJoin(direction, parentListId, {
      // -3 gets us to the parent
      left: paths.left.slice(0, -3),
      right: paths.right.slice(0, -3),
    });

    const destNode = direction === 'left' ? leftSideNode : rightSideNode;
    const srcNode = direction === 'right' ? leftSideNode : rightSideNode;
    const isStringLeft = isStringElement(leftSideNode);
    const isStringRight = isStringElement(rightSideNode);

    if (isStringLeft && isStringRight) {
      // When joining empty paragraph textNodes that contain a single ZWJ
      // character we want to make sure to remove them. They'll be added back
      // elsewhere if needed
      const leftSideValue = removeZWJChars(
        Expressions.getText(leftSideNode.data) || ''
      );
      const rightSideValue = removeZWJChars(
        Expressions.getText(rightSideNode.data) || ''
      );
      const newValue = cleanZWJChars(leftSideValue + rightSideValue);
      const updatedSubtree = pipe([
        updateData((data) => removeElementById(data, srcNode.id)),
        setStringValue(destNode.id, newValue),
      ])(subtree);

      // Note: It's possible for both sides of the selection to be in the node that's disappearing [src] node
      // like selecting an entire (b -> textNode) that's surrounded by textNodes and unbolding
      const updatedSelection = (() => {
        const rangeSelection = assertRangeSelectionRecordType(selection);

        // If there is no selection keep the value the same.
        if (!rangeSelection) return selection;

        const sel = getLeftRightPosition(rangeSelection);

        if (direction === 'left') {
          const updateLeftSideOfSelection =
            rightSideNode.id === sel.leftNodeId
              ? setLeftEndOfSelection(
                  leftSideNode.id,
                  leftSideValue.length + sel.leftOffset
                )
              : leftSideNode.id === sel.leftNodeId && !leftSideValue
                ? setLeftEndOfSelection(sel.leftNodeId, 0)
                : identity;

          const updateRightSideOfSelection =
            rightSideNode.id === sel.rightNodeId
              ? setRightEndOfSelection(
                  leftSideNode.id,
                  leftSideValue.length + sel.rightOffset
                )
              : leftSideNode.id === sel.rightNodeId && !leftSideValue
                ? setRightEndOfSelection(sel.leftNodeId, 0)
                : identity;

          return pipe([updateLeftSideOfSelection, updateRightSideOfSelection])(
            rangeSelection
          );
        }

        if (direction === 'right') {
          const updateLeftSideOfSelection =
            leftSideNode.id === sel.leftNodeId
              ? setLeftEndOfSelection(rightSideNode.id, sel.leftOffset)
              : identity;

          const updateRightSideOfSelection =
            leftSideNode.id === sel.rightNodeId
              ? setRightEndOfSelection(rightSideNode.id, sel.rightOffset)
              : rightSideNode.id === sel.rightNodeId
                ? setRightEndOfSelection(
                    rightSideNode.id,
                    leftSideValue.length + sel.rightOffset
                  )
                : identity;

          return pipe([updateLeftSideOfSelection, updateRightSideOfSelection])(
            rangeSelection
          );
        }

        return selection;
      })();

      const updatedState = state
        .set('subtree', updatedSubtree)
        .set('selection', updatedSelection);

      return recurse(updatedState);
    }

    if (!isStringLeft && !isStringRight) {
      // don't merge a br's children on the right side because it has none
      // just recurse and we'll merge it's parent's children
      if (isLineBreakElement(rightSideNode)) return recurse(state);

      const updatedSelection = (() => {
        const rangeSelection = assertRangeSelectionRecordType(selection);
        if (!rangeSelection || rangeSelection.anchorNodeId !== rightSideNode.id)
          return selection;

        // if we're not merging text nodes, and the caret is inside the right
        // side node (was just removed) then it was between linebreaks and
        // since we're merging most likely at the 0 index to the left of a
        // line break so then find the index of the leftSideNode to put the
        // caret in (either between linebreaks or in a textNode)
        const rightParent = Expressions.getParent(
          EElement(subtree),
          rightSideNode.id
        );
        if (!rightParent) return selection;
        const rightIndex = (getChildren(rightParent) || []).findIndex(
          (el) => el.id === rightSideNode.id
        );
        return moveToNeighbor('left', rightParent, rightIndex, subtree, state);
      })();

      const newChildren = (getChildren(leftSideNode) || []).concat(
        getChildren(rightSideNode) || []
      );
      let updatedSubtree = updateData(
        pipe([
          (expr) => removeElementById(expr, srcNode.id),
          (expr) =>
            Expressions.updateElement(
              destNode.id,
              setChildren(newChildren),
              expr
            ),
        ])
      )(subtree);

      // TODO (Alex): Keep in mind the possibility of other use cases other
      // than lists. Should we make this more generic?
      if (parentListId) {
        // If there is a parentListId, it means that the right edge of our
        // selection is at the last element of a list. If that's the case, we
        // make sure that the parent element is deleted if it's empty
        // inside of the updated subtree
        const updatedParentListNode = Expressions.getElementById(
          EElement(updatedSubtree),
          parentListId
        );
        const parentListNodeChildren = updatedParentListNode
          ? getChildren(updatedParentListNode)
          : undefined;

        if (parentListNodeChildren && parentListNodeChildren.length === 0) {
          updatedSubtree = updateData((expr) =>
            removeElementById(expr, parentListId)
          )(updatedSubtree);
        }
      }

      const updatedState = state
        .set('subtree', updatedSubtree)
        .set('selection', updatedSelection);

      return recurse(updatedState);
    }

    // For all other cases (e.g. left edge is on plain node paragraph text
    // and right edge on linked/strong/em text or vice versa), we want the
    // middle nodes just to be trimmed and won't need any actual node merge
    return state;
  };

export const deeplyJoinNodes =
  (
    leftNode: ExpressionElement,
    rightNode: ExpressionElement,
    direction: 'left' | 'right'
  ) =>
  (state: ContentEditorActive) => {
    const {subtree, selection} = state;
    let parentListNode: undefined | ExpressionElement;

    // If any of the edge nodes is a list node (ul) we need to raise the
    // hierarchy of its li to be the node considered for the mergepaths
    if (isList(leftNode) || isList(rightNode)) {
      const rangeSelection = assertRangeSelectionRecordType(selection);
      if (rangeSelection === null) return state;
      // We check the anchor node ancestors to be sure of which list node we'll
      // be swapping for the current leftNode or rightNode
      const anchorNodeAncestors = getAncestorIds(
        subtree.data,
        rangeSelection.anchorNodeId
      );
      const anchorListNode = getClosestListItemElement(
        subtree.data,
        rangeSelection.anchorNodeId
      );
      const focusListNode = getClosestListItemElement(
        subtree.data,
        rangeSelection.focusNodeId
      );

      if (isList(leftNode)) {
        leftNode = anchorNodeAncestors.includes(leftNode.id)
          ? anchorListNode || leftNode
          : focusListNode || leftNode;
      }

      if (isList(rightNode)) {
        parentListNode = rightNode;
        rightNode = anchorNodeAncestors.includes(parentListNode.id)
          ? anchorListNode || rightNode
          : focusListNode || rightNode;
      }
    }

    const mergePaths = getMergePaths(leftNode, rightNode, subtree);
    if (!mergePaths) return state;

    return pipe([
      // Note: deepJoin will recursively work until the nodes are joined
      // before moving onto the next step.
      deepJoin(direction, parentListNode?.id, mergePaths),
      bumpSelectionEdgesIntoNearbyTextNodes,
    ])(state);
  };

// TODO (Yoav): clean these names up, the deepJoin is only copying descendants, it should include the delete operation as well
// Note: this considers a merge operation and sometimes selects more appriate ancestors that would need to be merged, for the left and right
// node specified to be merged together.
// This should be used to merge two nodes that are siblings or the descendants of two siblings
export const mergeElementsSubtree =
  (leftElementId: string, rightElementId: string) =>
  (state: ContentEditorActive) => {
    const ancestors = getElementWithAncestors(state.subtree.data);
    const left = ancestors(leftElementId).find(
      (el) => !Expressions.allElements(EElement(el))[rightElementId]
    );
    const right = ancestors(rightElementId).find(
      (el) => !Expressions.allElements(EElement(el))[leftElementId]
    );
    if (!left || !right) return state;
    return deeplyJoinNodes(left, right, 'left')(state);
  };

// Note: with certain selections we may end up with multiple 'break out nodes'. eg: if breaking
// out on a span, and your selection starts in different links, each link will get a separate span
// nested within to obey the speicifc hierarchy we allow. Or wrapping a node that ends up getting merged with existing
// ancestors as its lifted to the proper hierarchy.
//
// We typically only break out right after wrapping textNodes. which means the bounds of the selection
// are within the node we just added (even if it got merged with an adjacent node)
// So we look for the left edge of the selection and then traverse up looking for the first instance
// of that tag, and then mark that as the node we're breaking out to.
export const breakOutToElementIfAllowed =
  (elementType: Qualified<Component.ComponentName>) =>
  (state: ContentEditorActive) => {
    if (
      !state.breakOutSpanLinks ||
      (!namesEqual(elementType, spanType) && !namesEqual(elementType, linkType))
    )
      return state;

    const selection = assertRangeSelectionRecordType(state.selection);
    if (!selection) return state;

    const {leftNodeId} = getLeftRightPosition(selection);
    const breakOutElement = Expressions.closestElement(
      state.subtree.data,
      leftNodeId,
      elementTypeEquals(elementType)
    );

    return breakOutElement
      ? state.set('breakOutElement', breakOutElement)
      : state;
  };

const formatToElementType = {
  link: linkType,
  span: spanType,
  bold: strongType,
  italic: emphasizedType,
  superscript: superscriptType,
  subscript: subscriptType,
  inlineCode: inlineCodeType,
};

const mergeWithAdjacentSiblingsReducer = (
  state: ContentEditorActive,
  el: ExpressionElement
) => mergeWithAdjacentSiblings(el.id)(state);

export const isFormatTogglePending = (state: ContentEditorActive) => {
  const {bold, italic} = state.formatToggle;
  return bold || italic;
};

export const resetPendingFormat = (state: ContentEditorActive) => {
  return state.set('formatToggle', PendingFormatToggle());
};

export const setConsecutiveSpacebarAttempts =
  (attempts: number) => (state: ContentEditorActive) =>
    state.set(
      'consecutiveSpacebarAttempts',
      attempts === CONSECUTIVE_SPACE_ATTEMPT_WARNING_THRESHOLD ? 0 : attempts
    );

export const insertChars = (chars: string) => (state: ContentEditorActive) => {
  const {selection} = state;

  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (!rangeSelection || !isCollapsed(rangeSelection)) return state;

  const anchorNode = Expressions.getElementById(
    state.subtree.data,
    rangeSelection.anchorNodeId
  );
  if (!anchorNode) return state;

  const textValue = Expressions.getText(anchorNode.data) || '';
  const insideEmptyTextNode = textValue === ZWJ_RAW;
  const currentTextValue = insideEmptyTextNode ? '' : textValue;
  const newTextValue = insert(
    currentTextValue,
    rangeSelection.anchorOffset,
    chars
  );
  // if contentEditor is collapsed and we just typed in a character, move selection offset number of characters
  const newOffset = insideEmptyTextNode
    ? chars.length
    : rangeSelection.anchorOffset + chars.length;

  const adjustedSelectionRecord = rangeSelection
    .set('anchorOffset', newOffset)
    .set('focusOffset', newOffset);

  return pipe([
    updateSubtree(
      updateData((data) =>
        Expressions.updateElement(
          rangeSelection.anchorNodeId,
          (el) => ({...el, data: EText(newTextValue)}),
          data
        )
      )
    ),
    setSelection(adjustedSelectionRecord),
    setConsecutiveSpacebarAttempts(0),
  ])(state);
};

// If in a 'new/empty' node that only contains a ZWJ character, select it,
// otherwise insert a ZWJ character and select it.
// eslint-disable-next-line webflow/selector-memoization
const selectZWJChar = (state: ContentEditorActive) => {
  const selection = assertRangeSelectionRecordType(state.selection);
  if (!selection) return state;
  const textNode = Expressions.getElementById(
    state.subtree.data,
    selection.anchorNodeId
  );

  if (textNode && Expressions.getText(textNode.data) === ZWJ_RAW)
    return state.set('selection', selectText(textNode.id, ZWJ_RAW)(selection));

  return pipe([
    insertChars(ZWJ_RAW),
    setSelection(moveRightSideOfSelection(1, selection)),
  ])(state);
};

// If we were in/outserting a format node on an empty paragraph we selectedthe
// the ZWJ character before the operation we handle the ZWJ characters
// insertChars deeplyJoinNodes so here we can just collapse that selection too
// put it backup the way it was
const collapseCaretNearZWJ = (state: ContentEditorActive) => {
  const selection = assertRangeSelectionRecordType(state.selection);
  if (!selection) return state;
  const collapsedSelection = collapseTextSelection(
    selection,
    selection.anchorNodeId,
    selection.anchorOffset
  );
  return state.set('selection', collapsedSelection);
};

export const applyPendingFormat = (
  stateAndId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const state = getFirst(stateAndId);
  if (!isFormatTogglePending(state)) return stateAndId;

  const {subtree, formatToggle} = state;
  const rangeSelection = assertRangeSelectionRecordType(state.selection);

  if (!rangeSelection || !isCollapsed(rangeSelection)) return stateAndId;

  const textNode = Expressions.getElementById(
    subtree.data,
    rangeSelection.anchorNodeId
  );

  if (!textNode) return stateAndId;

  const toggle =
    (format: 'bold' | 'italic') =>
    (_stateAndId: ContentEditorActiveAndNextId) => {
      const shouldToggle = formatToggle[format];
      if (!shouldToggle) return _stateAndId;
      const elementType = formatToElementType[format];

      if (shouldWrapTextNode(elementType)(subtree, textNode.id))
        return insertFormatNode(format, _stateAndId);

      return outsertFormatNode(format, _stateAndId);
    };

  return pipe([
    toggle('bold'),
    toggle('italic'),
    idPassthrough(resetPendingFormat),
  ])(stateAndId);
};

/*
Overview: Wrapping textNodes
1. split the textNodes that contain the edges of the range selection
2. mark all textNodes that are not already descendants of the tag to be processed
3. wrap any of the marked textNodes in the format tag eg: strong
4. raise all the format tags up to the proper hierarchy absorbing any intermediate nodes.
   eg: `em => strong => text`, becomes `strong => em => text`, where the new em is a possibly a new node
   and the old em is now a sibling of the strong node (the strong node broke through it)
5. once the format node (eg: strong) is at the proper hierarchy, then stop.
Note: This also makes sure the selection moves with each step so that it never references an invalid
node or offset as things get merged and split.
 */
export const wrapSelection =
  (format: FORMAT_BUTTON) =>
  ([state, seedId]: ContentEditorActiveAndNextId) => {
    const elementType = formatToElementType[format];

    const createFormatNode = (textNode: ExpressionElement, id: string) => {
      const nodeFactory = formatElementCreation[format];
      const [{id: nextId}, rawEl] = nodeFactory(id);

      const el = updateData((data) => {
        const props = Expressions.getRecord(data);
        if (!props) return data;

        const children = EList([EElement(textNode)]);

        if (format === 'link' && !state.breakOutSpanLinks)
          return ERecord({
            ...props,
            children,
            link: ELiteral({
              name: qualify(Plugins.name('Basic'), typeName('Link')),
              value: {mode: 'external', url: ''},
            }),
          });

        return ERecord({...props, children});
      })(rawEl);

      return pair(el, nextId);
    };

    const adjustedState = bumpSelectionEdgesInside(state);
    const splitResult = splitAndTrackSelectedTextNodes(
      adjustedState,
      shouldWrapTextNode(elementType),
      seedId
    );
    const nextState = splitResult.state;
    const {
      subtree: subtreeAfterWrapping,
      newElements,
      nextId,
    } = splitResult.trackedElements.reduce<{
      subtree: ExpressionElement;
      newElements: Array<ExpressionElement>;
      nextId: string;
    }>(
      (acc, textNode) => {
        const [newElement, freshId] = createFormatNode(textNode, acc.nextId);
        acc.nextId = freshId;
        acc.newElements.push(newElement);
        acc.subtree = replaceElement(textNode.id, newElement)(acc.subtree);
        return acc;
      },
      {
        subtree: nextState.subtree,
        newElements: [],
        nextId: splitResult.nextId,
      }
    );

    const [subtreeWithProperHierarcy, finalId] = newElements.reduce(
      raiseElementToProperHierarchy,
      pair(subtreeAfterWrapping, nextId)
    );

    const stateWithProperHierarcy = setSubtree(subtreeWithProperHierarcy)(
      nextState
    );

    // Note: When merging two nodes to the left, the right node is deleted. So
    // if a right node needed to merge with its sibling to the right (outside of
    // the selection) and we iterate from left to right by the time we got to
    // that last node to check its siblings it wouldn't exist anymore. We solve
    // that by using reduceRight() and starting with the right node to make sure
    // we process every one.
    const mergeFormatSiblings = (_state: ContentEditorActive) =>
      newElements.reduceRight(mergeWithAdjacentSiblingsReducer, _state);

    const finalState = pipe([
      mergeFormatSiblings,
      moveSelectionEdgesWithLineBreaks(state),
      breakOutToElementIfAllowed(elementType),
    ])(stateWithProperHierarcy);

    return pair(finalState, finalId);
  };

/*
Overview: Unwrapping textNodes
1. split the textNodes that contain the edges of the range selection
2. mark all textNodes within the selection that are descendants offset
   the elementType to be processed
3. raise all the marked textNodes, absorbing any intermediate nodes.
   eg: `strong => em => [text, text]`,
   becomes `[strong => em => leftText, em => rightText]`,
   where the new em is possibly a new element and a sibling of the strong.
4. once the textNode and ancestors (not including the elementType) are siblingsof
   of the ancestor of elementType, then stop.
Note: This also makes sure the selection moves with each step so that it never references an invalid
node or offset as things get merged and split.
 */
export const unwrapSelection =
  (format: FORMAT_BUTTON) => (stateAndNextId: ContentEditorActiveAndNextId) => {
    const elementType = formatToElementType[format];
    const state = getFirst(stateAndNextId);
    const seedId = getSecond(stateAndNextId);
    // If the edges of the selection are between line breaks that are at
    // different nesting depths, we want to bump the edges to be at the same
    // depth as the line break that's included in the selection.
    const adjustedState = bumpSelectionEdgesInside(state);
    const splitResult = splitAndTrackSelectedTextNodes(
      adjustedState,
      shouldUnwrapTextNode(elementType),
      seedId
    );
    const textNodesToUnwrap = splitResult.trackedElements;
    const raiseResult = raiseNodesOutOfFormatNode(
      textNodesToUnwrap,
      elementType,
      splitResult.state,
      splitResult.nextId
    );
    return pair(
      moveSelectionEdgesWithLineBreaks(adjustedState)(raiseResult.state),
      raiseResult.nextId
    );
  };

export const clearFormatting = pipe([
  unwrapSelection(FORMAT_BUTTONS.link),
  unwrapSelection(FORMAT_BUTTONS.span),
  unwrapSelection(FORMAT_BUTTONS.bold),
  unwrapSelection(FORMAT_BUTTONS.italic),
  unwrapSelection(FORMAT_BUTTONS.superscript),
  unwrapSelection(FORMAT_BUTTONS.subscript),
  unwrapSelection(FORMAT_BUTTONS.inlineCode),
]);

// This decides whether to wrap/unwrap nodes and then applies that change
export const toggleSelectionFormat =
  (format: FORMAT_BUTTON) => (stateAndNextId: ContentEditorActiveAndNextId) => {
    const [state] = stateAndNextId;
    const {selection} = state;
    const rangeSelection = assertRangeSelectionRecordType(selection);

    if (rangeSelection === null) return stateAndNextId;

    const predicate = isToolbarFormatType[format];
    const hasTextLevelNodesToWrap = selectionHasTextLevelNodesToWrap(
      state.subtree,
      rangeSelection,
      predicate
    );

    if (hasTextLevelNodesToWrap) return wrapSelection(format)(stateAndNextId);

    const selectedNodes = getSelectedElements(state.subtree, rangeSelection);
    const matchingSelectedNodes = selectedNodes.some(predicate);

    if (!matchingSelectedNodes) return stateAndNextId;

    return unwrapSelection(format)(stateAndNextId);
  };

// When there's a collapsed caret and the user hits cmd+b or cmd+i, we want to
// insert the appropriate tag using insertFormatNode (or remove the given tag with outsertFormatNode)
// possibly splitting the current text node and maintaining proper hierarchy
const insertOrOutsertFormatNode =
  (
    wrapOrUnwrap: (
      format: FORMAT_BUTTON
    ) => (
      stateAndId: ContentEditorActiveAndNextId
    ) => ContentEditorActiveAndNextId
  ) =>
  (
    format: 'bold' | 'italic',
    stateAndId: ContentEditorActiveAndNextId
  ): ContentEditorActiveAndNextId => {
    const state = getFirst(stateAndId);
    const selection = assertRangeSelectionRecordType(state.selection);

    // Note: if the selection was not collapsed you probably should have calledwrapSelection
    // wrapSelection or unwrapSelection instead
    if (!selection || !isCollapsed(selection)) return stateAndId;

    return pipe([
      idPassthrough(selectZWJChar),
      wrapOrUnwrap(format),
      idPassthrough(collapseCaretNearZWJ),
    ])(stateAndId);
  };

export const insertFormatNode = insertOrOutsertFormatNode(wrapSelection);
export const outsertFormatNode = insertOrOutsertFormatNode(unwrapSelection);

const removeTag = dissoc('tag');

const changeElementFormatType = (
  el: ExpressionElement,
  format: 'p' | 'blockquote' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
) => {
  const props = Expressions.getRecord(el.data);
  if (!props) return el;

  if (format === 'blockquote' || format === 'p')
    return {
      ...el,
      type: format === 'p' ? paragraphType : blockquoteType,
      data: ERecord(removeTag(props)),
    };

  return {
    ...el,
    type: headingType,
    data: ERecord({...props, tag: EEnum(format)}),
  };
};

/*
Should toggle top level typographic nodes tag of selected content using splitAndTrackSelectedTextNodes()

Overview:
1. Get the nodes in the current selection
2. We iterate these nodes and look for top level typographic nodes
   Tags for top level typographic nodes: p, h1, h2, h3, h4, h5, h6, blockquote.
3. If the current selection has any top level typo node with the passed tag
   we should toggle that tag into a paragraph and ignore all other top level nodes
   Otherwise, convert the tags from the selected top level typo nodes into the passed tag.
*/
export const toggleTopLevelElementFormat = (
  format: 'blockquote' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6',
  state: ContentEditorActive
) => {
  const {subtree} = state;
  const selection = assertRangeSelectionRecordType(state.selection);
  if (!selection) return state;

  const selectedElements = getSelectedElements(subtree, selection);
  const isFormatType = isToolbarFormatType[format];
  const selectionHasFormat = Boolean(selectedElements.find(isFormatType));
  const newSubtree = selectedElements.reduce(
    (_subtree: ExpressionElement, el: ExpressionElement) => {
      if (!isRichTextTopLevelTypographic(el)) return _subtree;
      const shouldChangeToParagraph = selectionHasFormat && isFormatType(el);

      if (
        shouldChangeToParagraph ||
        (!selectionHasFormat && !isFormatType(el))
      ) {
        const newFormat = shouldChangeToParagraph ? 'p' : format;
        const newEl = changeElementFormatType(el, newFormat);
        return replaceElement(el.id, newEl)(_subtree);
      }

      return _subtree;
    },
    subtree
  );

  return setSubtree(newSubtree)(state);
};

/*
Will first try to deeply merge a node with it's sibling to the right (if it can)
Will then try to deeply merge a node (or if it merged right, the result of that)
with it's sibling to the left (if it can)
 */
export const mergeWithAdjacentSiblings =
  (nodeId: string) => (state: ContentEditorActive) => {
    const {subtree} = state;
    const node = Expressions.getElementById(subtree.data, nodeId);

    // In some cases the node would already have been merged with neighbors and
    // may not exist anymore, so we can just continue
    if (!node) {
      return state;
    }

    const parentNode = Expressions.getParent(EElement(subtree), nodeId);
    if (!parentNode) return state;

    const siblings = getChildren(parentNode);
    if (!siblings) return state;

    const nodeIndex = siblings.findIndex((el) => el.id === nodeId);
    const leftSibling = siblings[nodeIndex - 1];
    const rightSibling = siblings[nodeIndex + 1];
    const shouldMergeLeft = leftSibling && shouldMerge(node, leftSibling);
    const shouldMergeRight = rightSibling && shouldMerge(node, rightSibling);

    if (leftSibling && shouldMergeLeft && !shouldMergeRight)
      // merge with left sibling only
      return deeplyJoinNodes(leftSibling, node, 'left')(state);

    if (rightSibling && shouldMergeRight && !shouldMergeLeft)
      // merge with right sibling only
      return deeplyJoinNodes(node, rightSibling, 'right')(state);

    if (leftSibling && rightSibling && shouldMergeLeft && shouldMergeRight)
      // merge with both siblings
      return pipe([
        deeplyJoinNodes(node, rightSibling, 'right'),
        deeplyJoinNodes(leftSibling, rightSibling, 'left'),
      ])(state);

    return state;
  };

export const raiseNodeOutsideFormatNode =
  (elementType: Qualified<Component.ComponentName>) =>
  (
    acc: {state: ContentEditorActive; nextId: string},
    el: ExpressionElement
  ) => {
    const predicate = elementTypeEquals(elementType);

    const absorbParent = (
      element: ExpressionElement,
      parent: ExpressionElement,
      nextId: string
    ) =>
      predicate(parent)
        ? {element, nextId}
        : {
            element: {...setChildren([element])(parent), id: nextId},
            nextId: incrementGuid(nextId),
          };

    const {targetElement, subtree, nextId} = raiseNodeAbsorbingAncestors(
      predicate,
      absorbParent,
      el,
      acc.state.subtree,
      acc.nextId
    );

    return {
      state: pipe([
        mergeWithAdjacentSiblings(targetElement.id),
        moveSelectionEdgesWithLineBreaks(acc.state),
      ])(acc.state.set('subtree', subtree)),
      nextId,
    };
  };

export const updateElementDataAt =
  (
    elementId: string | null | undefined,
    path: ExpressionPath,
    newExpr: Expression
  ) =>
  (state: ContentEditorActive) => {
    const {subtree} = state;

    if (!elementId || !subtree) return state;

    const updated = Expressions.updateElement(
      elementId,
      (el) => {
        const newData = Expressions.setIn(el.data, path, newExpr);

        return {
          ...el,
          data: newData,
        };
      },
      EElement(subtree)
    );

    const updatedEl = Expressions.getElement(updated);

    return updatedEl ? state.set('subtree', updatedEl) : state;
  };

// Below are functions that work directly on the `subtree` without State being included.

/**
 * Removes empty text elements and real elements with empty children including.
 */
export const cleanUpGhostLinebreaksAndEmptyText = (
  subtree: ExpressionElement
): ExpressionElement =>
  filterElementTree(subtree, (el) => {
    const isText = isStringElement(el);

    // Remove empty text elements. Text elements containing "&zwnj;" are considered non-empty and
    // are not removed.
    // ie. val === ''
    if (isText) {
      const text = Expressions.getText(Expressions.getElementData(el)) || '';
      if (text.length === 0) {
        return false;
      }
    }

    const children = getChildren(el);

    // Remove real elements w/ empty children.
    // ie. <strong></strong>
    // @TODO Figure out a safer way to do this that accounts for Videos in CMS Rich Text.
    // if (
    //   !isText &&
    //   !isLineBreakElement(el) &&
    //   children &&
    //   children.length === 0
    // ) {
    //   return false;
    // }

    // Remove real elements with children, but all of them are linebreaks or empty text.
    // ie. <strong><br></strong>
    // ie. <strong>''</strong>
    if (children && children.length > 0) {
      let realElementCount = 0;

      for (const child of children) {
        const text =
          Expressions.getText(Expressions.getElementData(child)) || '';

        if (
          (!isLineBreakElement(child) && !isStringElement(child)) ||
          (isStringElement(child) && text.length > 0)
        ) {
          realElementCount++;
        }
      }

      return realElementCount > 0;
    }

    return true;
  });

export const getFigureIdFromSelection = (
  subtree: ExpressionElement,
  selection: SelectionRecordType
): string | null => {
  const nodeSelection = assertNodeSelectionRecordType(selection);

  if (nodeSelection?.nodeId) {
    return nodeSelection.nodeId ?? null;
  }

  const rangeSelection = assertRangeSelectionRecordType(selection);

  if (rangeSelection?.anchorNodeId) {
    const figureElement = getClosestFigureElement(
      subtree.data,
      rangeSelection.anchorNodeId
    );
    if (figureElement) {
      return figureElement.id;
    }
  }
  return null;
};

const figureTypePath: ExpressionPath = [
  {in: 'Record', at: 'figure'},
  {in: 'Record', at: 'type'},
];

export const getFigureType = (
  figureElement: ExpressionElement
): string | null => {
  return Expressions.getElementDataAt(
    figureElement,
    figureTypePath,
    'Text',
    null
  );
};

const figureMaxWidthPath: ExpressionPath = [
  {in: 'Record', at: 'figure'},
  {in: 'Record', at: 'maxWidth'},
];

export const setInFigureMaxWidth = (value: string) => (data: Expression) =>
  Expressions.sparseSetIn(data, figureMaxWidthPath, EText(value));

const figureMaxHeightPath: ExpressionPath = [
  {in: 'Record', at: 'figure'},
  {in: 'Record', at: 'maxHeight'},
];

export const setInFigureMaxHeight = (value: string) => (data: Expression) =>
  Expressions.sparseSetIn(data, figureMaxHeightPath, EText(value));

const figureAlignPath: ExpressionPath = [
  {in: 'Record', at: 'figure'},
  {in: 'Record', at: 'align'},
];

export const setInFigureAlign = (value: string) => (data: Expression) =>
  Expressions.sparseSetIn(data, figureAlignPath, EText(value));

const styleWidthPath: ExpressionPath = [
  {in: 'Record', at: 'style'},
  {in: 'Record', at: 'width'},
];

export const setInFigureWidthStyle = (value: string) => (data: Expression) =>
  Expressions.sparseSetIn(data, styleWidthPath, EText(value));

const styleMaxWidthPath: ExpressionPath = [
  {in: 'Record', at: 'style'},
  {in: 'Record', at: 'max-width'},
];

export const setInFigureMaxWidthStyle = (value: string) => (data: Expression) =>
  Expressions.sparseSetIn(data, styleMaxWidthPath, EText(value));

const stylePaddingBottomPath: ExpressionPath = [
  {in: 'Record', at: 'style'},
  {in: 'Record', at: 'padding-bottom'},
];

export const setInFigurePaddingBottom = (value: string) => (data: Expression) =>
  Expressions.sparseSetIn(data, stylePaddingBottomPath, EText(value));

const removeConsecutiveSpacesExpressionMatcher = matchExpression({
  Element: (el: ExpressionElement): Expression => {
    if (!isStringElement(el)) return EElement(el);
    const text = Expressions.getText(Expressions.getElementData(el)) || '';
    const updatedData = Expressions.setElementData(
      el,
      EText(text.replace(/ {2}/g, ' ' + NBSP_RAW))
    );
    return EElement(updatedData);
  },
  _: (expr: Expression) => expr,
});

/**
 * Removes consecutive spaces in text elements.
 */
export const removeConsecutiveSpaces = (
  subtree: ExpressionElement
): ExpressionElement => {
  const updatedSubtreeExpr = Expressions.everywhere(
    removeConsecutiveSpacesExpressionMatcher
  )(EElement(subtree));

  const updatedSubtree = Expressions.getElement(updatedSubtreeExpr);
  return updatedSubtree ?? subtree;
};

// Unwrap a figure from a link
export const removeFigureLink = (
  linkNodeId: string,
  state: ContentEditorActive
) => {
  const {subtree} = state;
  if (!linkNodeId) return state;

  // Find the image inside of the figure
  const imageElement = findDeepFigureChildElement(subtree, linkNodeId);
  if (imageElement === null) return state;

  // Find the parent div block for the image
  const imageParentElement = Expressions.getParent(
    EElement(subtree),
    imageElement.id
  );
  if (imageParentElement === null) return state;

  return updateSubtree(
    // Replace the figure link element with the image parent element
    replaceElement(linkNodeId, imageParentElement)
  )(state);
};

// Removes a wrapping link from the currently selected figure
export const removeLinkFromFigureSettings = (
  state: ContentEditorActive,
  figureId: string
) => {
  const {subtree, selection} = state;

  const selectedNodeId = getFigureIdFromSelection(subtree, selection);
  if (!figureId) {
    return state;
  }

  const figureElement = Expressions.getElementById(EElement(subtree), figureId);
  if (!figureElement) {
    return state;
  }

  const linkDescendantElement = findElementDescendant(figureElement, isLink);
  if (linkDescendantElement) {
    // If the current figure has an active link, we remove it
    if (selectedNodeId === figureId) {
      state = state.set('activeToolbar', TOOLBARS.FIGURESETTINGS);
    }
    const linkDescendantElementId = Expressions.getElementId(
      linkDescendantElement
    );
    return removeFigureLink(linkDescendantElementId, state);
  } else {
    // If the current figure doesn't have any links yet, we just pass state
    return state;
  }
};

// Empty text nodes have ZWJ characters in them. Without those ZWJ characters
// the native caret would refuse to exist inside the native text node. We use
// these 'Empty' textNodes inside rich text elements when creating a new line
// (which is really a paragraph with a ZWJ textNode) (this is when we show the
// add Toolbar), and in simple and rich text when adding a line break at the end
// of a format/typo node we'll add a ZWJ textNode after the line break so that
// the line break is visible (if the line break is the last node in its parent
// it  doesn't create a new line natively and is effectively ignored)

// This function bumps the caret to the left or right of the ZWJ character and
// should be used to help give the impression that there is no ZWJ character
export const bumpCaretAroundZWJChar =
  (direction: 'left' | 'right') => (state: ContentEditorActive) => {
    const {selection, subtree} = state;

    if (selection.type !== 'Range' || !isCollapsed(selection)) {
      return state;
    }

    const charBeforeCaret = getCharBeforeCaret(subtree, selection);
    const charAfterCaret = getCharAfterCaret(subtree, selection);

    if (
      (direction === 'left' && charBeforeCaret === ZWJ_RAW) ||
      (direction === 'right' && charAfterCaret === ZWJ_RAW)
    ) {
      const delta = direction === 'right' ? 1 : -1;
      return changeCaretOffsetBy(delta)(state);
    }

    return state;
  };

export const hideToolbar = (state: ContentEditorActive) =>
  state
    .set('activeToolbar', null)
    .set('slashCommand', {...state.slashCommand, isMenuFocused: false});

export const handleAddListElementOnTab = (
  stateAndId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const state = getFirst(stateAndId);
  const seedId = getSecond(stateAndId);
  const {subtree} = state;
  const selection = assertRangeSelectionRecordType(state.selection);
  if (!selection) return stateAndId;

  const list = Expressions.closestElement(
    subtree.data,
    selection.anchorNodeId,
    isList
  );
  if (!list) return stateAndId;

  const listItem = Expressions.closestElement(
    list.data,
    selection.anchorNodeId,
    isListItem
  );
  if (!listItem) return stateAndId;

  const props = Expressions.getRecord(list.data);
  if (!props || !props.tag) return stateAndId;
  const listType = Expressions.getEnum(props.tag) || 'ul';
  if (listType !== 'ul' && listType !== 'ol') return stateAndId;

  const withPrevItem = (
    prevItem: ExpressionElement
  ): ContentEditorActiveAndNextId => {
    const prevItemChildren = getChildren(prevItem) || [];
    const nestedList = prevItemChildren.find(isList);

    const updateItems = (
      prevItemUpdate: (el: ExpressionElement) => ExpressionElement
    ) =>
      updateSubtree(
        updateParentChildren(prevItem.id, (items) => {
          const index = items.indexOf(prevItem);
          if (index < 0) return items;
          return [
            ...items.slice(0, index),
            prevItemUpdate(prevItem),
            // + 2 because we are omitting listItem
            ...items.slice(index + 2),
          ];
        })
      );

    // add list items to existing nested list in previous list rather than creating new list.
    if (nestedList) {
      return pair(
        updateItems(
          updateChildren(nestedList.id, (children) =>
            append(children, listItem)
          )
        )(state),
        seedId
      );
    }

    const brResult = createLineBreak(seedId);
    const br = getSecond(brResult);
    const listId = getFirst(brResult).id;

    const newList = {
      id: listId,
      type: qualify(Plugins.name('Basic'), Component.name('List')),
      data: ERecord({
        tag: EEnum(listType),
        list: ERecord({
          type: EText('list'),
        }),
        children: EList([EElement(listItem)]),
      }),
    };

    return pair(
      pipe([
        updateItems(setChildren(prevItemChildren.concat([br, newList]))),
        setSelection(
          moveCaretToDeepStartOfNode(listItem.id)(subtree) || selection
        ),
        hideToolbar,
      ])(state),
      incrementGuid(listId)
    );
  };

  return maybe(stateAndId)(withPrevItem)(getPrev(listItem.id)(EElement(list)));
};

// inserts elements after the closest top level element to the selection
// also gives the indexes of where it was inserted.
export const insertElementsAfterClosestTopLevel = (
  elementsToInsert: ExpressionElement[],
  state: ContentEditorActive
): {
  state: ContentEditorActive;
  indexFromStart: number;
  indexFromEnd: number;
} => {
  let indexFromStart = 0;
  let indexFromEnd = 0;

  const {subtree, selection} = state;

  const rangeSelection = assertRangeSelectionRecordType(selection);

  if (rangeSelection === null) return {state, indexFromStart, indexFromEnd};

  const closestTopLevelElement = getClosestTopLevelElement(
    rangeSelection.anchorNodeId,
    subtree
  );

  if (closestTopLevelElement === null) {
    // replace all contents with new elements
    return {
      state: updateSubtree(setChildren(elementsToInsert))(state),
      indexFromStart,
      indexFromEnd,
    };
  }

  const path = Expressions.findPath(
    EElement(subtree),
    (expr) => getExprElementId(expr) === closestTopLevelElement.id
  );

  if (path === null) {
    console.error('anchor element not found for element insertion');
    return {state, indexFromStart, indexFromEnd};
  }

  return {
    state: pipe([
      setSubtree(
        contentEditorGetElement(
          updateElementListAtPath(EElement(subtree), path, (list, index) => {
            const elementsBefore = list.slice(0, index + 1);
            const elementsAfter = list.slice(index + 1);
            indexFromStart = elementsBefore.length;
            indexFromEnd = 0 - elementsAfter.length;

            return EList([
              ...elementsBefore,
              ...elementsToInsert.map(EElement),
              ...elementsAfter,
            ]);
          })
        )
      ),
      updateSubtree((_subtree: ExpressionElement) => {
        if (isEmptyTypoOrLineBreak(closestTopLevelElement)) {
          if (indexFromStart > 0) {
            indexFromStart--;
          }
          return contentEditorGetElement(
            removeElementById(EElement(_subtree), closestTopLevelElement.id)
          );
        }
        return _subtree;
      }),
    ])(state),
    indexFromStart,
    indexFromEnd,
  };
};

// Get figure type ('video' or 'image')
export const getFigureTypeById = (
  subtree: ExpressionElement,
  elementId: string
): string | null => {
  const figureElement = Expressions.getElementById(subtree.data, elementId);
  if (!figureElement) return null;
  return getFigureType(figureElement);
};

// Updates a video iframes title text
export const setVideoTitle = (
  subtree: ExpressionElement,
  elementId: string,
  value: string
): ExpressionElement => {
  const iframeElement = findDeepFigureChildElement(subtree, elementId);
  if (!iframeElement) return subtree;

  const result = Expressions.updateElement(
    iframeElement.id,
    updateData((data) =>
      Expressions.setIn(
        data,
        [
          {in: 'Record', at: 'attr'},
          {in: 'Record', at: 'title'},
        ],
        EText(value)
      )
    ),
    EElement(subtree)
  );

  const updatedSubtree = Expressions.getElement(result);
  return updatedSubtree || subtree;
};

export function raiseChildrenToParent(
  tree: ExpressionElement,
  id: string
): ExpressionElement {
  const targetElement = Expressions.getElementById(tree.data, id);
  if (!targetElement) return tree;

  const targetChildren = getChildren(targetElement) ?? [];

  return updateParentChildren(id, (offspring) => {
    const index = offspring.findIndex((el) => el.id === id);
    if (index < 0) return offspring;
    return [
      ...offspring.slice(0, index),
      ...targetChildren,
      ...offspring.slice(index + 1),
    ];
  })(tree);
}

function raiseElementToAbsorbingParent(
  isDone: (el: ExpressionElement, parent: ExpressionElement) => boolean,
  absorbParentEl: (
    el: ExpressionElement,
    parent: ExpressionElement,
    nextId: string
  ) => {element: ExpressionElement; nextId: string},
  element: ExpressionElement,
  enforcementPair: ElementAndId
): ElementAndId {
  const [subtree, seedId] = enforcementPair;
  const parent = Expressions.getParent(subtree.data, element.id);

  if (!parent || isDone(element, parent)) return enforcementPair;

  // Handle same type parent by unpacking children
  if (namesEqual(parent.type, element.type)) {
    return [raiseChildrenToParent(subtree, element.id), seedId];
  }

  // Handle element that needs to be raised
  const result = raiseNodeOneLevel(
    absorbParentEl,
    element,
    parent,
    subtree,
    seedId
  );

  // Recursively raise the middle element if needed
  return raiseElementToAbsorbingParent(
    isDone,
    absorbParentEl,
    result.targetElement,
    [result.subtree, result.nextId]
  );
}

/**
 * Creates a new element structure where the parent becomes a child of the element.
 * The element's children are moved to the parent, and the parent becomes the only
 * child of the element.
 *
 * Example:
 * Input:
 *   <em>              (parent)
 *     <strong>         (element)
 *       <text>1</text>
 *       <text>2</text>
 *     </strong>
 *   </em>
 *
 * Output:
 *   <strong>           (element)
 *     <em>           (parent)
 *       <text>1</text>
 *       <text>2</text>
 *     </em>
 *   </strong>
 *
 * @param element - The element that will become the new parent
 * @param parent - The current parent that will become a child
 * @returns A new element with the restructured hierarchy
 */
const absorbParentElement = (
  elementToLift: ExpressionElement,
  currentParent: ExpressionElement,
  nextId: string
) => {
  const children = getChildren(elementToLift) ?? emptyArray;
  const newParent = setChildren(children)(currentParent);
  return {
    element: setChildren([{...newParent, id: nextId}])(elementToLift),
    nextId: incrementGuid(nextId),
  };
};

export function raiseElementToProperHierarchy(
  enforcementPair: ElementAndId,
  element: ExpressionElement
): ElementAndId {
  return raiseElementToAbsorbingParent(
    isValidParent,
    absorbParentElement,
    element,
    enforcementPair
  );
}

function findIncorrectlyNestedFormatElements(subtree: ExpressionElement) {
  return reduceElementTree(
    subtree,
    (formatElements: ExpressionElement[], element: ExpressionElement) => {
      const parent = Expressions.getParent(subtree.data, element.id);
      if (
        parent &&
        isFormatElement(element) &&
        !isValidParent(element, parent)
      ) {
        formatElements.push(element);
      }
      return formatElements;
    },
    []
  );
}

function sortFormatElementHierarchy(
  enforcementPair: ElementAndId
): ElementAndId {
  const [subtree] = enforcementPair;
  const formatElements = findIncorrectlyNestedFormatElements(subtree);

  const updatedPair = formatElements.reduceRight(
    (
      currentPair: ElementAndId,
      formatElement: ExpressionElement
    ): ElementAndId => {
      // Earlier moves may have changed this element's children or removed it.
      const currentElement = Expressions.getElementById(
        currentPair[0].data,
        formatElement.id
      );
      return currentElement
        ? raiseElementToProperHierarchy(currentPair, currentElement)
        : currentPair;
    },
    enforcementPair
  );
  if (updatedPair[0] !== enforcementPair[0]) {
    return sortFormatElementHierarchy(updatedPair);
  }
  return updatedPair;
}

function findInvalidSimpleTextElements(
  opts: EnforceOptionsType,
  subtree: ExpressionElement
): ExpressionElement[] {
  const {allowLinksInContent} = opts || {};

  return findAllDescendantsFromExpression(subtree.data, (element) => {
    const illegalAnchor = !allowLinksInContent && isLinkElement(element);
    const invalidElement = illegalAnchor || !isSimpleTextElement(element);
    return !isStringElement(element) && invalidElement;
  });
}

export function unpackInvalidElementsInSimpleText(opts: EnforceOptionsType) {
  return function unpackInvalidElementsInSimpleTextWithOpts(
    subtree: ExpressionElement
  ): ExpressionElement {
    const invalids = findInvalidSimpleTextElements(opts, subtree);
    const result = invalids.reduceRight((tree, invalid) => {
      return raiseChildrenToParent(tree, invalid.id);
    }, subtree);

    if (result !== subtree) {
      return unpackInvalidElementsInSimpleTextWithOpts(result);
    }
    return result;
  };
}

function findFormatAndTextElements(
  subtree: ExpressionElement
): ExpressionElement[] {
  return findAllDescendantsFromExpression(subtree.data, isFormatOrTextElement);
}

function mergeAdjacementFormatElements(
  subtree: ExpressionElement
): ExpressionElement {
  const formatElements = findFormatAndTextElements(subtree);

  return formatElements.reduceRight(
    (tree: ExpressionElement, element: ExpressionElement, index: number) => {
      if (index === 0) return tree;
      const parent = Expressions.getParent(EElement(tree), element.id);
      if (!parent) return tree;
      const parentChildren = getChildren(parent) || emptyArray;
      const childIndex = findIndexById(element.id, parentChildren);
      if (childIndex === 0) return tree;

      const prevSibling = parentChildren[childIndex - 1];
      if (!prevSibling) return tree;
      if (shouldMerge(prevSibling, element)) {
        const mockState = activeContentEditor({
          subtree: tree,
          selection: NodeSelectionRecord(emptyObject),
        });
        return deeplyJoinNodes(prevSibling, element, 'left')(mockState).subtree;
      }
      return tree;
    },
    subtree
  );
}

export type EnforceOptionsType = {
  allowLinksInContent?: boolean;
};

// This is only expected to happen by pasting figures into figcaptions, in which
// case we just want to remove them. It's unlikely imported content or content pasted
// from another source will otherwise have this structure, so no need to preserve them.
const removeFiguresInFigcaptions = (
  subtree: ExpressionElement
): ExpressionElement => {
  const figuresInFigcaptions = findAllDescendantsFromExpression(
    subtree.data,
    (element) =>
      isFigure(element) &&
      Expressions.closestElement(subtree.data, element.id, isFigCaption) !==
        null
  );
  return figuresInFigcaptions.reduce(
    (tree: ExpressionElement, figure: ExpressionElement) =>
      updateData((data) => removeElementById(data, figure.id))(tree),
    subtree
  );
};

const findFigureContentElements = (
  subtree: ExpressionElement
): ExpressionElement[] => {
  return findAllDescendantsFromExpression(
    subtree.data,
    (element) => isImageElement(element) || isIframeElement(element)
  );
};

function wrapFigureContentInFigureWithExistingFigure(
  closestFigureElement: ExpressionElement,
  imgOrIframe: ExpressionElement,
  subtreeAndId: ElementAndId
): ElementAndId {
  const contentElementsInFigure =
    findFigureContentElements(closestFigureElement);
  const [subtree, seedId] = subtreeAndId;
  if (contentElementsInFigure.length > 1) {
    const instantiatedFigure = instantiateElement(
      {
        id: 'Figure',
        type: qualify(Plugins.name('Basic'), Component.name('Figure')),
        data: ERecord({
          figure: ERecord({
            align: EText('center'),
            type: EText('image'),
          }),
          children: EList([
            EElement({
              id: 'Block',
              type: qualify(Plugins.name('Basic'), Component.name('Block')),
              data: ERecord({
                tag: EEnum('div'),
                children: EList([EElement(imgOrIframe)]),
              }),
            }),
          ]),
        }),
      },
      seedId
    );
    const newFigure = getSecond(instantiatedFigure);
    const nextId = getFirst(instantiatedFigure);

    return [
      {
        ...subtree,
        data: addElementAtAnchor(
          removeElementById(subtree.data, imgOrIframe.id),
          newFigure,
          closestFigureElement,
          'after'
        ),
      },
      nextId.id,
    ];
  }

  let contentBlock = imgOrIframe;
  let oldContentBlock = imgOrIframe;
  let nextId = seedId;
  const parent = Expressions.getParent(subtree.data, imgOrIframe.id);
  if (!parent) return subtreeAndId;
  if (isDivBlock(parent)) {
    contentBlock = parent;
    oldContentBlock = contentBlock;

    const grandparent = Expressions.getParent(subtree.data, parent.id);
    if (grandparent && isLinkElement(grandparent)) {
      contentBlock = grandparent;
      oldContentBlock = contentBlock;
    }
  } else {
    const initiatedWrappedElement = instantiateElement(
      {
        id: 'Block',
        type: qualify(Plugins.name('Basic'), Component.name('Block')),
        data: ERecord({
          tag: EEnum('div'),
          children: EList([EElement(imgOrIframe)]),
          text: EBoolean(false),
        }),
      },
      seedId
    );
    nextId = getFirst(initiatedWrappedElement).id;
    contentBlock = getSecond(initiatedWrappedElement);
  }

  return [
    {
      ...subtree,
      data: addElementAtAnchor(
        removeElementById(subtree.data, oldContentBlock.id),
        contentBlock,
        closestFigureElement,
        'prepend'
      ),
    },
    nextId,
  ];
}

function wrapFigureContentImageInFigure(
  imgOrIframe: ExpressionElement,
  subtreeAndId: ElementAndId
): ElementAndId {
  const [tree, seedId] = subtreeAndId;
  const initiatedWrappedElement = instantiateElement(
    {
      id: 'Figure',
      type: qualify(Plugins.name('Basic'), Component.name('Figure')),
      data: ERecord({
        figure: ERecord({
          align: EText('center'),
          type: EText('image'),
        }),
        children: EList([
          EElement({
            id: 'Block',
            type: qualify(Plugins.name('Basic'), Component.name('Block')),
            data: ERecord({
              tag: EEnum('div'),
              children: EList([EElement(imgOrIframe)]),
            }),
          }),
        ]),
      }),
    },
    seedId
  );
  const newFigure = getSecond(initiatedWrappedElement);
  return [
    replaceElement(imgOrIframe.id, newFigure)(tree),
    getFirst(initiatedWrappedElement).id,
  ];
}

function wrapFigureContentVideoInFigure(
  imgOrIframe: ExpressionElement,
  subtreeAndId: ElementAndId
): ElementAndId {
  const [subtree, seedId] = subtreeAndId;
  const initiatedWrappedElement = instantiateElement(
    {
      id: 'Figure',
      type: qualify(Plugins.name('Basic'), Component.name('Figure')),
      data: ERecord({
        figure: ERecord({
          align: EText('center'),
          dimensions: EText(''),
          maxHeight: EText(''),
          maxWidth: EText(''),
          pageUrl: EText(''),
          type: EText('video'),
        }),
        style: ERecord({
          paddingBottom: EText(''),
        }),
        children: EList([
          EElement({
            id: 'Block',
            type: qualify(Plugins.name('Basic'), Component.name('Block')),
            data: ERecord({
              tag: EEnum('div'),
              children: EList([EElement(imgOrIframe)]),
            }),
          }),
        ]),
      }),
    },
    seedId
  );
  const newFigure = getSecond(initiatedWrappedElement);

  return [
    replaceElement(imgOrIframe.id, newFigure)(subtree),
    getFirst(initiatedWrappedElement).id,
  ];
}

function wrapFigureContentInFigure(
  subtreeAndId: ElementAndId,
  imgOrIframe: ExpressionElement
): ElementAndId {
  const [subtree] = subtreeAndId;
  const closestFigureElement = Expressions.closestElement(
    subtree.data,
    imgOrIframe.id,
    isFigure
  );

  if (closestFigureElement)
    return wrapFigureContentInFigureWithExistingFigure(
      closestFigureElement,
      imgOrIframe,
      subtreeAndId
    );

  if (isImageElement(imgOrIframe))
    return wrapFigureContentImageInFigure(imgOrIframe, subtreeAndId);

  return wrapFigureContentVideoInFigure(imgOrIframe, subtreeAndId);
}

const wrapAllFigureContentInFigures = ([
  subtree,
  seedId,
]: ElementAndId): ElementAndId => {
  const subtreeWithoutFiguresInFigcaptions =
    removeFiguresInFigcaptions(subtree);

  const figureContentElements = findFigureContentElements(
    subtreeWithoutFiguresInFigcaptions
  );
  return figureContentElements.reduceRight(wrapFigureContentInFigure, [
    subtreeWithoutFiguresInFigcaptions,
    seedId,
  ]);
};

const findEmptyFigureElements = (
  subtree: ExpressionElement
): ExpressionElement[] => {
  return findAllDescendantsFromExpression(subtree.data, (element) => {
    return isFigure(element) && findFigureContentElements(element).length === 0;
  });
};

function reparentChildrenInParagraph(
  [subtree, seedId]: ElementAndId,
  targetElement: ExpressionElement
): ElementAndId {
  const childrenExpression = Expressions.getIn(targetElement.data, [
    {in: 'Record', at: 'children'},
  ]);

  const childrenAsList = EList(
    childrenExpression ? (Expressions.getList(childrenExpression) ?? []) : []
  );

  const [uniqueIdsState, newParagraph] = instantiateElement(
    {
      id: 'Paragraph',
      type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
      data: ERecord({children: childrenAsList}),
    },
    seedId
  );

  const updatedSubtree = replaceElement(
    targetElement.id,
    newParagraph
  )(subtree);

  return [updatedSubtree, uniqueIdsState.id];
}

function convertContentlessFiguresToParagraphs(
  elementAndId: ElementAndId
): ElementAndId {
  const subtree = getFirst(elementAndId);
  const emptyFigures = findEmptyFigureElements(subtree);
  return emptyFigures.reduce(reparentChildrenInParagraph, elementAndId);
}

function findFigurelessFigcaptions(
  subtree: ExpressionElement
): ExpressionElement[] {
  return findAllDescendantsFromExpression(
    subtree.data,
    (element) =>
      isFigCaption(element) &&
      !Expressions.closestElement(subtree.data, element.id, isFigure)
  );
}

function convertFigurelessFigcaptionsToParagraphs([
  subtree,
  seedId,
]: ElementAndId): ElementAndId {
  const figurelessFigcaptions = findFigurelessFigcaptions(subtree);

  return figurelessFigcaptions.reduce(reparentChildrenInParagraph, [
    subtree,
    seedId,
  ]);
}

// Cheap precondition for enforcement passes that operate on a specific
// element type (e.g. figures, figcaptions, line breaks). Iterates the
// already-cached `Expressions.allElements` map and short-circuits at the
// first match. Lets passes early-exit before kicking off their full
// per-element find work, which is the dominant cost of paste enforcement
// on a large rich text element. See enforceRichTextStructure.
const treeContainsAny = (
  subtree: ExpressionElement,
  predicate: (element: ExpressionElement) => boolean
): boolean => {
  const elements = Expressions.allElements(subtree.data);
  for (const id in elements) {
    const element = elements[id];
    if (element && predicate(element)) return true;
  }
  return false;
};

function findStrayFormatElements(
  subtree: ExpressionElement
): Set<ExpressionElement> {
  const elements = Object.values(Expressions.allElements(subtree.data));
  return elements.reduce(
    (
      strayFormatElements: Set<ExpressionElement>,
      element: ExpressionElement
    ) => {
      const parent = Expressions.getParent(subtree.data, element.id);
      if (!parent) {
        return strayFormatElements;
      }
      const children = getChildren(parent);
      const isFirstChild = children && children[0] === element;

      const closestFigureElement = Expressions.closestElement(
        subtree.data,
        element.id,
        isFigure
      );
      const closestFigcaptionElement = Expressions.closestElement(
        subtree.data,
        element.id,
        isFigCaption
      );

      const isFirstLinkInFigure =
        isLinkElement(element) && isFigure(parent) && isFirstChild;

      if (
        isFormatOrTextElement(element) &&
        closestFigureElement &&
        !closestFigcaptionElement &&
        !isFirstLinkInFigure
      ) {
        const newParent = findFurthestAncestor(
          subtree.data,
          element,
          isFormatOrTextElement
        );

        if (newParent) {
          strayFormatElements.add(newParent);
        }
      }

      return strayFormatElements;
    },
    new Set()
  );
}

function groupStrayFormatElementsAroundFirstFigcaption(
  subtree: ExpressionElement,
  strayFormatElements: Set<ExpressionElement>
): [ExpressionElement[], ExpressionElement[]] {
  return Array.from(strayFormatElements).reduce(
    ([before, after]: [ExpressionElement[], ExpressionElement[]], el) => {
      const figureElement = Expressions.closestElement(
        subtree.data,
        el.id,
        isFigure
      );
      const figcaptionElement =
        figureElement && findElement(figureElement, isFigCaption);

      // If there isn't one, it will be created later
      if (!figcaptionElement) {
        before.push(el);
        return [before, after];
      }

      const flattened = flatten(figureElement);
      const figcaptionIndex = findIndexById(figcaptionElement.id, flattened);
      const elementIndex = findIndexById(el.id, flattened);

      if (elementIndex < figcaptionIndex) {
        before.push(el);
      } else {
        after.push(el);
      }
      return [before, after];
    },
    [[], []]
  );
}

function wrapFigureTextContentInFigcaption(
  tagEnforcementPair: ElementAndId,
  element: ExpressionElement,
  position: 'append' | 'prepend'
): ElementAndId {
  const [subtree, seedId] = tagEnforcementPair;
  const figureElement = Expressions.closestElement(
    subtree.data,
    element.id,
    isFigure
  );
  if (!figureElement) return tagEnforcementPair;
  const figureCaptionElement = findElement(figureElement, isFigCaption);
  const withRemovedElement = removeElementById(subtree.data, element.id);

  if (figureCaptionElement) {
    return [
      {
        ...subtree,
        data: addElementAtAnchor(
          withRemovedElement,
          element,
          figureCaptionElement,
          position
        ),
      },
      seedId,
    ];
  } else {
    const [{id: nextSeedId}, newFigcaption] = instantiateElement(
      {
        id: 'Figcaption',
        type: qualify(Plugins.name('Basic'), Component.name('Figcaption')),
        data: ERecord({children: EList([EElement(element)])}),
      },
      seedId
    );
    const [firstChild] = getChildren(figureElement) ?? [];
    if (!firstChild) return tagEnforcementPair;
    return [
      {
        ...subtree,
        data: addElementAtAnchor(
          withRemovedElement,
          newFigcaption,
          firstChild,
          'after'
        ),
      },
      nextSeedId,
    ];
  }
}

function wrapAllFigureTextContentInFigcaption(
  tagEnforcementPair: ElementAndId
): ElementAndId {
  const [subtree] = tagEnforcementPair;
  // findStrayFormatElements only returns matches when there's a figure to
  // host them, so trees without any figure can never produce stray format
  // elements. The find walk itself is O(N²) on large trees because it
  // calls Expressions.closestElement(isFigure) per element, so skip it.
  if (!treeContainsAny(subtree, isFigure)) {
    return tagEnforcementPair;
  }
  const strayFormatElements = findStrayFormatElements(subtree);

  if (!strayFormatElements.size) {
    return tagEnforcementPair;
  }

  const [beforeFigcaption, afterFigcaption] =
    groupStrayFormatElementsAroundFirstFigcaption(subtree, strayFormatElements);

  // If it came before the figcaption we are reparenting, we'll insert it to the start of the figcaption
  // we do this in right to left order to preserve the order of the nodes with prepend
  //
  // If there is no figcaption, we'll insert one at the end of the figure for subsequent elements
  const before = beforeFigcaption.reduceRight(
    (nextTagEnforcementPair: ElementAndId, element: ExpressionElement) => {
      return wrapFigureTextContentInFigcaption(
        nextTagEnforcementPair,
        element,
        'prepend'
      );
    },
    tagEnforcementPair
  );

  // If it came after the figcaption we are reparenting, we'll insert it to the end of the figcaption with append
  // we can do this in left to right order with append
  return afterFigcaption.reduce(
    (nextTagEnforcementPair: ElementAndId, element: ExpressionElement) => {
      return wrapFigureTextContentInFigcaption(
        nextTagEnforcementPair,
        element,
        'append'
      );
    },
    before
  );
}

const wrapSublevelElementsAtTopLevelInParagraphs = (
  tagEnforcementPair: ElementAndId
): ElementAndId => {
  const [subtree] = tagEnforcementPair;
  const children = getChildren(subtree) ?? emptyArray;

  // wrap all non-top-level-nodes at the top-level in a paragraph
  return children.reduce(
    (nextEnforcementPair: ElementAndId, child: ExpressionElement) => {
      const [nextSubtree, seedId] = nextEnforcementPair;
      if (!isRichTextTopLevelElement(nextSubtree)(child)) {
        const [{id: nextSeedId}, newParagraph] = instantiateElement(
          {
            id: 'Paragraph',
            type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
            data: ERecord({
              enforce: ERecord({
                merge: EBoolean(true),
              }),
              children: EList([EElement(child)]),
            }),
          },
          seedId
        );

        return [
          replaceElement(child.id, newParagraph)(nextSubtree),
          nextSeedId,
        ];
      }
      return nextEnforcementPair;
    },
    tagEnforcementPair
  );
};

const removeMisplacedLineBreakElements = (
  enforcementPair: ElementAndId
): ElementAndId => {
  const [subtree] = enforcementPair;
  // No line break elements => nothing to remove. Skip the per-element
  // parent lookup that the find walk would otherwise do.
  if (!treeContainsAny(subtree, isLineBreakElement)) {
    return enforcementPair;
  }
  const strayLineBreaks = findAllDescendantsFromExpression(
    subtree.data,
    (element) => {
      const parent = Expressions.getParent(subtree.data, element.id);
      return Boolean(
        isLineBreakElement(element) && parent && !isValidParent(element, parent)
      );
    }
  );
  return strayLineBreaks.reduce(
    (
      [tree, seedId]: ElementAndId,
      lineBreak: ExpressionElement
    ): ElementAndId => {
      return [
        {
          ...tree,
          data: removeElementById(tree.data, lineBreak.id),
        },
        seedId,
      ];
    },
    enforcementPair
  );
};

type ListItemId = string;
type NestedListId = string;
type ListItemNestedListPair = [
  parentItem: ListItemId,
  nestedList: NestedListId,
];

const findListItemNestedListPairs = (
  element: ExpressionElement
): Array<ListItemNestedListPair> => {
  const lists = findAllDescendantsFromExpression(element.data, isList);

  type ListTracker = {
    lastListItem: ListItemId | null;
    pairs: Array<ListItemNestedListPair>;
  };
  return lists.flatMap((listElement) => {
    const children = getChildren(listElement) ?? [];
    return children.reduce(
      (acc: ListTracker, child: ExpressionElement): ListTracker => {
        if (isList(child) && acc.lastListItem) {
          acc.pairs.push([acc.lastListItem, child.id]);
        }

        if (isListItem(child)) {
          acc.lastListItem = child.id;
        }

        return acc;
      },
      {
        lastListItem: null,
        pairs: [],
      }
    ).pairs;
  });
};

const createSeparatorFragment = (seedId: string): [Expression[], string] => {
  const [{id: nextSeedId}, separator] = instantiateExpressionWithUniqueIds(
    EList([
      EElement({
        id: 'EmptyString',
        type: qualify(Plugins.name('Basic'), Component.name('String')),
        data: EText(' '),
      }),
      EElement({
        id: 'LineBreak',
        type: qualify(Plugins.name('Basic'), Component.name('LineBreak')),
        data: ERecord({}),
      }),
    ])
  )(seedId);

  const separators = Expressions.getList(separator) ?? [];

  return [separators, nextSeedId];
};

const nestListsInListItems = (enforcementPair: ElementAndId): ElementAndId => {
  const [subtree] = enforcementPair;
  const pairs = findListItemNestedListPairs(subtree);

  return pairs.reduce(
    (
      nextEnforcementPair: ElementAndId,
      [listItemId, nestedListId]: ListItemNestedListPair
    ): ElementAndId => {
      const [element, currentSeedId] = nextEnforcementPair;
      const nestedList = Expressions.getElementById(element.data, nestedListId);

      if (!nestedList) {
        return nextEnforcementPair;
      }

      const lastListItem = Expressions.getElementById(element.data, listItemId);
      if (!lastListItem) return nextEnforcementPair;

      const [separators, nextSeedId] = createSeparatorFragment(currentSeedId);
      const updatedElementData = Expressions.updateElement(
        lastListItem.id,
        updateData((data) => {
          return Expressions.updateIn(
            data,
            [{in: 'Record', at: 'children'}],
            (children) => {
              const childrenArray = Expressions.getList(children) ?? [];
              const lastChild = childrenArray[childrenArray.length - 1];
              const lastChildEl =
                lastChild && Expressions.getElement(lastChild);
              const hasValidSeparator =
                lastChildEl && isStringElement(lastChildEl);
              if (hasValidSeparator) {
                return EList([...childrenArray, EElement(nestedList)]);
              }
              return EList([
                ...childrenArray,
                ...separators,
                EElement(nestedList),
              ]);
            }
          );
        }),
        removeElementById(element.data, nestedListId)
      );

      return [
        {
          ...element,
          data: updatedElementData,
        },
        nextSeedId,
      ];
    },
    enforcementPair
  );
};

const replaceTopLevelTyposNestedInListItemsToListItems = (
  enforcementPair: ElementAndId
): ElementAndId => {
  const [subtree] = enforcementPair;
  const topLevelTyposInListItems = findAllDescendantsFromExpression(
    subtree.data,
    (element) => {
      return Boolean(
        isRichTextTopLevelElement(subtree)(element) &&
        !isList(element) &&
        !isFigure(element) &&
        Expressions.closestElement(subtree.data, element.id, isListItem)
      );
    }
  );

  return topLevelTyposInListItems.reduce(
    (
      [currentSubtree, currentSeedId]: ElementAndId,
      element: ExpressionElement
    ): ElementAndId => {
      const children =
        Expressions.getIn(element.data, [{in: 'Record', at: 'children'}]) ??
        EList([]);
      const [{id: nextSeedId}, newListItem] = instantiateElement(
        {
          id: 'ListItem',
          type: qualify(Plugins.name('Basic'), Component.name('ListItem')),
          data: ERecord({
            list: ERecord({
              type: EText('item'),
            }),
            text: EBoolean(false),
            children,
          }),
        },
        currentSeedId
      );

      return [
        replaceElement(element.id, newListItem)(currentSubtree),
        nextSeedId,
      ];
    },
    enforcementPair
  );
};

const addNew =
  (factory: (id: string) => Pair<UniqueIdsState, ExpressionElement>) =>
  (anchorId: string) =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const [{id}, el] = factory(getSecond(stateAndId));

    return pair(
      pipe<ContentEditorActive>([
        updateSubtree(insertElement(el, anchorId, 'after')),
        removeElement(anchorId),
        (state) =>
          state.set(
            'selection',
            moveCaretToDeepStartOfNode(el.id)(state.subtree) ?? state.selection
          ),
      ])(getFirst(stateAndId)),
      id
    );
  };

export const addNewHeading = (anchorId: string, type: number) => {
  if (
    type === 1 ||
    type === 2 ||
    type === 3 ||
    type === 4 ||
    type === 5 ||
    type === 6
  )
    return addNew(headingFactory(`h${type}`))(anchorId);

  return identity;
};

export const addNewBlockquote = addNew(blockquoteFactory);

const attrStartPath: ExpressionPath = [
  {in: 'Record', at: 'attr'},
  {in: 'Record', at: 'start'},
];

export const addNewList =
  (anchorId: string, listType: 'ul' | 'ol') =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const factory = (seedId: string) => {
      const result = listFactory(listType)(seedId);
      if (listType !== 'ol') return result;

      const state = getFirst(stateAndId);

      const selection = assertRangeSelectionRecordType(state.selection);
      if (!selection) return result;

      const anchor = Expressions.getElementById(
        EElement(state.subtree),
        selection.anchorNodeId
      );
      if (!anchor) return result;

      const text = Expressions.getText(anchor.data) || '';
      if (!text) return result;

      const [idMap, list] = result;
      const start = text.slice(0, -1);

      return pair(
        idMap,
        updateData((data) =>
          Expressions.sparseSetIn(data, attrStartPath, EText(start))
        )(list)
      );
    };

    return addNew(factory)(anchorId)(stateAndId);
  };

const addLineBreak =
  (anchor: string) =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const brId = getSecond(stateAndId);
    const br = {
      id: brId,
      type: qualify(Plugins.name('Basic'), Component.name('LineBreak')),
      data: ERecord({}),
    };
    return pair(
      updateSubtree(insertElement(br, anchor, 'append'))(getFirst(stateAndId)),
      incrementGuid(brId)
    );
  };

export const addListElement =
  (listAddType: typeof ADD_UL | typeof ADD_OL) =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const listType = listAddType === ADD_UL ? 'ul' : 'ol';
    const {subtree, selection} = getFirst(stateAndId);
    const selectedNodeId = getNodeIdFromSelection(selection);

    if (selectedNodeId === null) {
      console.error('no node id in selection when adding list');
      return stateAndId;
    }

    const closestListItemNode = getClosestListItemElement(
      EElement(subtree),
      selectedNodeId
    );

    if (closestListItemNode) {
      const children = getChildren(closestListItemNode) || [];
      const lastChild = children[children.length - 1];
      // if the list item isn't empty, then add the nested list to this list item.
      if (lastChild && children.length > 1)
        return pipe([
          addNewList(lastChild.id, listType),
          idPassthrough(hideToolbar),
        ])(stateAndId);

      // if the list item is empty, find previous list item and add nested list to that.
      const prevSiblingListItem = maybe<null | ExpressionElement>(
        null
      )<ExpressionElement>(identity)(
        getPrev(closestListItemNode.id)(EElement(subtree))
      );

      if (prevSiblingListItem) {
        const brId = getSecond(stateAndId);
        return pipe([
          addLineBreak(prevSiblingListItem.id),
          idPassthrough(removeElement(closestListItemNode.id)),
          addNewList(brId, listType),
          idPassthrough(hideToolbar),
        ])(stateAndId);
      }
    }

    // if not in a list, find empty paragraph / figure and add list action was called
    const typoNode = getClosestTopLevelTypoElement(selectedNodeId, subtree);
    if (typoNode)
      return pipe([
        addNewList(typoNode.id, listType),
        idPassthrough(hideToolbar),
      ])(stateAndId);

    return stateAndId;
  };

function findInvalidElementsInFigcaption(
  subtree: ExpressionElement
): ExpressionElement[] {
  return findAllDescendantsFromExpression(subtree.data, (element) => {
    const isInvalidNode = !(
      isStringElement(element) || isFigcaptionFormatElement(element)
    );

    const parent = Expressions.getParent(subtree.data, element.id);
    if (!parent) {
      return false;
    }

    const inFigcaption = Boolean(
      Expressions.closestElement(subtree.data, parent.id, isFigCaption)
    );

    return isInvalidNode && inFigcaption;
  });
}

function unpackInvalidNodesInFigcaptionElements([
  subtree,
  seedId,
]: ElementAndId): ElementAndId {
  // No figcaptions => no invalid-in-figcaption nodes to find. The find
  // walk does an Expressions.closestElement(isFigCaption) per element
  // (O(N²)), which dominates paste enforcement on large trees.
  if (!treeContainsAny(subtree, isFigCaption)) {
    return [subtree, seedId];
  }
  const invalidElementsInFigcaptions = findInvalidElementsInFigcaption(subtree);

  const updatedSubtree = invalidElementsInFigcaptions.reduceRight(
    (tree: ExpressionElement, element: ExpressionElement) =>
      raiseChildrenToParent(tree, element.id),
    subtree
  );
  return [updatedSubtree, seedId];
}

function isValidFigureDiv(
  subtree: ExpressionElement,
  element: ExpressionElement
): boolean {
  const isDiv = isDivBlock(element);
  const parent = Expressions.getParent(subtree.data, element.id);
  const figureDiv = isDiv && parent && isFigure(parent);
  const grandParent = parent && Expressions.getParent(subtree.data, parent.id);
  const linkedFigureDiv =
    isDiv &&
    parent &&
    isLinkElement(parent) &&
    grandParent &&
    isFigure(grandParent);

  return Boolean(figureDiv || linkedFigureDiv);
}

const convertStrayDivsToTopLevelTypos = (
  tagEnforcementPair: ElementAndId
): ElementAndId => {
  const [subtree] = tagEnforcementPair;

  const strayDivs = findAllDescendantsFromExpression(
    subtree.data,
    (element) => isDivBlock(element) && !isValidFigureDiv(subtree, element)
  );

  return strayDivs.reduce(
    (
      [currentSubtree, currentSeedId]: ElementAndId,
      element: ExpressionElement
    ): ElementAndId => {
      const firstTopLevelTyographicElement = findElement(
        element,
        (childElement: ExpressionElement) => {
          return isRichTextTopLevelElement(subtree)(childElement);
        }
      );

      const nextType = firstTopLevelTyographicElement
        ? firstTopLevelTyographicElement.type
        : qualify(Plugins.name('Basic'), Component.name('Paragraph'));

      return [
        {
          ...currentSubtree,
          data: Expressions.updateElement(
            element.id,
            (el) => {
              // Carry forward the specific heading tag since that Element can be h1..6
              const potentialTag =
                firstTopLevelTyographicElement &&
                isHeading(firstTopLevelTyographicElement) &&
                Expressions.getIn(
                  Expressions.getElementData(firstTopLevelTyographicElement),
                  [{in: 'Record', at: 'tag'}]
                );

              const withTag = potentialTag
                ? updateData((data) => {
                    return Expressions.setIn(
                      data,
                      [{in: 'Record', at: 'tag'}],
                      potentialTag
                    );
                  })(el)
                : el;
              return {
                ...withTag,
                type: nextType,
              };
            },
            currentSubtree.data
          ),
        },
        currentSeedId,
      ];
    },
    tagEnforcementPair
  );
};

/**
 * Finds elements that are incorrectly nested within the content structure.
 * Returns an array of element IDs that need to be moved to maintain proper hierarchy.
 *
 * @param subtree - The root element of the subtree to search
 * @returns Array of element IDs that are considered "stray" and need restructuring
 */
const findStrayElements = (subtree: ExpressionElement): string[] => {
  return findAllDescendantsFromExpression(subtree.data, (element) => {
    // Format elements can be nested anywhere
    if (isFormatElement(element)) {
      return false;
    }

    const parent = Expressions.getParent(subtree.data, element.id);

    // If there's no parent, it's not considered stray because it's a top level element
    if (!parent) {
      return false;
    }

    // Check if parent is valid based on element type:
    // - For text nodes: must be inside valid text containers
    // - For other elements: must follow specific parent-child rules
    const validParent = isStringElement(element)
      ? isValidTextElementParent(parent)
      : isValidParent(element, parent);

    // Check if this element is meant to be a top-level element
    // (like paragraphs, headings) but is nested deeper
    const isTopLevelNodeAtSubLevel =
      isRichTextTopLevelElement(subtree)(element);

    // An element is considered stray if either:
    // 1. Its parent is not in the list of valid parents for this element type
    // 2. It's a top-level element (like paragraph) nested under another element,
    //    EXCEPT when it's a list wrapper (to preserve list structure)
    return !validParent || (isTopLevelNodeAtSubLevel && !isList(element));
  }).map((element) => element.id);
};

/**
 * Finds the nearest ancestor that's a valid container for lists or top-level text elements.
 * Returns null if none is found.
 */
function findValidAncestorForMove(
  subtree: ExpressionElement,
  startParent: ExpressionElement
): ExpressionElement | null {
  return (
    Expressions.closestElement(subtree.data, startParent.id, isListItem) ||
    Expressions.closestElement(subtree.data, startParent.id, isList) ||
    Expressions.closestElement(
      subtree.data,
      startParent.id,
      isRichTextTopLevelTypographic
    ) ||
    null
  );
}

function shouldSplitAncestor(
  subtree: ExpressionElement,
  ancestorTopLevelElement: ExpressionElement,
  elementToMove: ExpressionElement
): boolean {
  // Check if the ancestor list item contains a nested list
  const listItemHasNestedList =
    isListItem(ancestorTopLevelElement) &&
    findAllDescendantsFromExpression(ancestorTopLevelElement.data, isList)
      .length > 0;

  // Check if there are other top-level elements besides the one we're moving
  const hasOtherTopLevelElements =
    findAllDescendantsFromExpression(
      ancestorTopLevelElement.data,
      (candidate) =>
        (isRichTextTopLevelElement(subtree)(candidate) ||
          isListItem(candidate)) &&
        candidate.id !== elementToMove.id
    ).length > 0;

  const ancestorTopLevelElementChildren =
    getChildren(ancestorTopLevelElement) ?? [];
  const firstElement = ancestorTopLevelElementChildren[0] ?? null;

  // Get the text value of the first element if it's a string element
  const firstElementValue =
    firstElement && isStringElement(firstElement)
      ? Expressions.getText(firstElement.data)
      : null;

  // Check if the element to move is the first element
  const isFirstElement = firstElement === elementToMove;

  // Check if the element to move is the second element and the first is empty
  const isFirstRealElement =
    ancestorTopLevelElementChildren[1] === elementToMove &&
    isEmptyValue(firstElementValue);

  // Determine if we should split the ancestor based on various conditions
  return (
    // Split if it's not a nested list and has other top-level elements or is the first (real) element
    (!listItemHasNestedList &&
      (hasOtherTopLevelElements || isFirstElement || isFirstRealElement)) ||
    // Always split for figures and list wrappers
    isFigure(elementToMove) ||
    isList(elementToMove)
  );
}

/**
 * Given the current tree data, the parent, and elementToMove,
 * return the new ExpressionNode with the element split out.
 *
 * Example:
 * Input:  <p id="p1">Some text <h2 id="h1">Nested heading</h2> More text</p>
 * Output: <p id="p1">Some text</p> <h2 id="h1">Nested heading</h2> <p id="p2">More text</p>
 *
 * In this example:
 * - subtree: The root element containing the paragraph
 * - parent: The paragraph element <p id="p1">
 * - elementToMove: The heading element <h2 id="h1">
 *
 * The function will:
 * 1. Split the parent paragraph into two parts
 * 2. Insert the heading between these two parts
 * 3. Return the updated subtree with the new structure
 */
function splitOutElement(
  subtree: ExpressionElement,
  parent: ExpressionElement,
  elementToMove: ExpressionElement,
  seedId: string
): ElementAndId {
  const nextId = incrementGuid(seedId);
  const parentChildren = getChildren(parent) ?? [];
  const elementIndex = findIndexById(elementToMove.id, parentChildren);

  // Create left side of the split (content before the heading)
  const leftSideElement = updateElementChildren(parent, (children) => {
    const childrenList = Expressions.getList(children) ?? [];
    return EList(childrenList.slice(0, elementIndex));
  });

  // Create right side of the split (content after the heading)
  const rightSideElement =
    parentChildren.length - 1 > elementIndex
      ? {
          ...updateElementChildren(parent, (children) => {
            const childrenList = Expressions.getList(children) ?? [];
            return EList(childrenList.slice(elementIndex + 1));
          }),
          id: nextId, // This will be the new id, e.g. p2
        }
      : null;

  return [
    pipe<ExpressionElement>([
      // Replace original parent with left side
      replaceElement(parent.id, leftSideElement),
      // Add right side after left side (if it exists)
      rightSideElement
        ? updateData((data) =>
            addElementAtAnchorById(data, rightSideElement, parent.id, 'after')
          )
        : identity,
      // Insert the moved element (heading) between left and right sides
      updateData((data) =>
        addElementAtAnchorById(data, elementToMove, parent.id, 'after')
      ),
    ])(subtree),
    nextId,
  ];
}

/**
 * Restructures the content tree by moving misplaced top-level typography elements
 * (like headings, paragraphs) to their correct positions in the hierarchy.
 *
 * Example:
 * Input:  <p>Some text <h2>Nested heading</h2> More text</p>
 * Output: <p>Some text</p> <h2>Nested heading</h2> <p>More text</p>
 *
 * This function ensures that headings and other top-level elements are not
 * nested inside paragraphs or other containers where they shouldn't be.
 */
function breakTopLevelTyposToTheTopLevel(
  enforcementPair: ElementAndId
): ElementAndId {
  const [subtree] = enforcementPair;
  const elementsToMove = findStrayElements(subtree);

  const updatedPair = elementsToMove.reduceRight(
    (elAndId: ElementAndId, elementToMoveId: string): ElementAndId => {
      const [nextSubtree, seedId] = elAndId;
      const elementToMove = Expressions.getElementById(
        nextSubtree.data,
        elementToMoveId
      );
      const parent = Expressions.getParent(nextSubtree.data, elementToMoveId);

      if (!elementToMove || !parent) return elAndId;

      // Check if element is a type that should be at the top level
      if (
        isPotentialRichTextTopLevelElement(elementToMove) ||
        isList(elementToMove) ||
        isListItem(elementToMove) ||
        isIframeElement(elementToMove)
      ) {
        // Find the nearest ancestor that's a valid container
        const ancestorTopLevelElement = findValidAncestorForMove(
          nextSubtree,
          parent
        );

        if (!ancestorTopLevelElement) {
          return elAndId;
        }

        if (
          shouldSplitAncestor(
            nextSubtree,
            ancestorTopLevelElement,
            elementToMove
          )
        ) {
          return splitOutElement(nextSubtree, parent, elementToMove, seedId);
        } else {
          // If no split needed, just replace element with its children
          return [raiseChildrenToParent(nextSubtree, elementToMove.id), seedId];
        }
      }

      return elAndId;
    },
    enforcementPair
  );

  // If any changes were made, recursively process again until no more changes needed
  if (updatedPair[0] !== enforcementPair[0]) {
    return breakTopLevelTyposToTheTopLevel(updatedPair);
  }

  return updatedPair;
}

function shouldEnforceMerge(el: ExpressionElement): boolean {
  return Boolean(
    Expressions.getIn(Expressions.getElementData(el), [
      {in: 'Record', at: 'enforce'},
      {in: 'Record', at: 'merge'},
    ])
  );
}

const mergeNewlyWrappedAdjacentParagraphs = ([
  subtree,
  seedId,
]: ElementAndId): ElementAndId => {
  const children = getChildren(subtree) ?? [];
  const stubSelectionToMakeUseOfDeeplyJoinNodes =
    NodeSelectionRecord(emptyObject);
  return [
    children.reduceRight((nextSubtree, el, i) => {
      const canMerge = shouldEnforceMerge(el);
      const leftSibling = i > 0 && children[i - 1];
      const canMergeLeft = leftSibling && shouldEnforceMerge(leftSibling);
      if (canMerge && canMergeLeft) {
        const state = activeContentEditor({
          subtree: nextSubtree,
          selection: stubSelectionToMakeUseOfDeeplyJoinNodes,
        });
        return deeplyJoinNodes(leftSibling, el, 'left')(state).subtree;
      }
      return nextSubtree;
    }, subtree),
    seedId,
  ];
};

export function getLinkModeFromHref(
  href: string
): 'external' | 'tel' | 'email' | 'section' {
  if (/^mailto:/.test(href)) return 'email';
  if (/^tel:/.test(href)) return 'tel';
  if (href.length > 1 && /^#/.test(href)) return 'section';
  return 'external';
}

// This does a best effort to extract the linkData (node.data.link)
// from the href
const getLinkDataFromHref = (href: string): Json => {
  const anchorType = getLinkModeFromHref(href);

  if (anchorType === 'email')
    return {
      mode: anchorType,
      email: parseEmailAddress(href) || '',
      subject: parseEmailSubject(href) || '',
    };

  if (anchorType === 'tel')
    return {
      mode: anchorType,
      tel: parseTel(href) || '',
    };

  if (anchorType === 'section')
    return {
      mode: anchorType,
      domId: '',
    };

  return {
    mode: anchorType,
    url: href,
  };
};

export const isEmptyLinkElement = (el: ExpressionElement) => {
  if (!isLinkElement(el)) return false;

  // Old comment: We check for emailOrPhoneDynBinding to allow empty links without the
  // default payload on dyn-bound phone or email fields, we want to respect
  // that for conditional visibility
  // New comment: If the link has any bindings we consider it not empty
  const hasBindings = Object.keys(getElementBindingDataPaths(el)).length > 0;
  if (hasBindings) return false;

  const props = Expressions.getRecord(el.data);
  // Malformed link; don’t mess with it
  if (!props) return false;

  const linkData = props.link ? Expressions.getLiteral(props.link) : null;

  const dataObj = linkData ? getObject(linkData.value) : undefined;
  // Malformed link; don’t mess with it
  if (!dataObj) return false;

  const {mode, pageId, domId, collectionId, itemId, url, email, tel, assetId} =
    dataObj;

  if (mode === 'external') return !url;
  // doesn't link to a static or detail page
  if (mode === 'page') return !pageId && !(itemId && collectionId);
  if (mode === 'section') return !domId;
  if (mode === 'email') return !email;
  if (mode === 'tel') return !tel;
  if (mode === 'file') return !assetId;

  return false;
};

const getLinkData = (settings: LinkToolbarChanges): Json => {
  const {
    href,
    pageId,
    itemId,
    collectionId,
    anchorType: mode,
    domId,
    email,
    tel,
    subject,
    target,
  } = settings;

  if (mode === 'external') {
    if (!href) return {mode};
    return target ? {mode, url: href, target} : {mode, url: href};
  }

  if (mode === 'page') {
    const data: Record<string, Json> =
      itemId && collectionId
        ? {mode, itemId, collectionId}
        : pageId
          ? {mode, pageId}
          : {mode};
    return target ? {...data, target} : data;
  }

  if (mode === 'section') {
    if (!domId) return {mode};
    return {mode, domId};
  }

  if (mode === 'email') {
    if (!email) return {mode};
    return subject ? {mode, email, subject} : {mode, email};
  }

  if (mode === 'tel') {
    return tel ? {mode, tel} : {mode};
  }

  return {mode: mode || 'external'};
};

export const updateLinkNode = (settings: LinkToolbarChanges) =>
  updateData((linkData) => {
    const {href, target, rel} = settings;
    const attrs: Record<string, Expression> = {};
    if (href) attrs.href = EText(href);
    if (target) attrs.target = EText(target);
    if (rel) attrs.rel = EText(rel);

    return ERecord({
      ...Expressions.getRecord(linkData),
      link: ELiteral({
        name: qualify(Plugins.name('Basic'), typeName('Link')),
        value: getLinkData(settings),
      }),
      attr: ERecord(attrs),
    });
  });

const DEFAULT_LINK_PAYLOAD = {
  anchorType: 'external',
  href: '#',
} as const;

export const handleLinkDefaults = (subtree: ExpressionElement) => {
  if (isEmptyLinkElement(subtree)) {
    return updateLinkNode(DEFAULT_LINK_PAYLOAD)(subtree);
  } else {
    return subtree;
  }
};

const ensureLinksHaveDefaultData = updateData(
  Expressions.everywhere(
    matchExpression({
      Element: (el) => {
        if (!isLinkElement(el)) return EElement(el);
        const props = Expressions.getRecord(el.data);
        if (!props) return EElement(el);
        const linkData = props.link ? Expressions.getLiteral(props.link) : null;
        if (props.link && !linkData) return EElement(el);
        const dataObj = linkData ? getObject(linkData.value) : undefined;
        if (
          !linkData ||
          (dataObj && dataObj.mode === 'external' && !dataObj.url)
        ) {
          const hrefExpr = props.attr
            ? Expressions.getRecord(props.attr)?.href
            : null;
          if (!hrefExpr) return EElement(el);
          const href = Expressions.getText(hrefExpr);
          if (!href) return EElement(el);
          return EElement({
            ...el,
            data: ERecord({
              ...props,
              link: ELiteral({
                name: qualify(Plugins.name('Basic'), typeName('Link')),
                value: getLinkDataFromHref(href),
              }),
            }),
          });
        }
        return EElement(el);
      },
      _: identity,
    })
  )
);

const getAlignment = (alignExpr: Expression | null): string => {
  return alignExpr
    ? (Expressions.getText(alignExpr) ?? DEFAULT_FIGURE_ALIGNMENT)
    : DEFAULT_FIGURE_ALIGNMENT;
};

const ensureFigureHasDefaultData = (figure: ExpressionElement) =>
  updateData((data) => {
    const props = Expressions.getRecord(data);
    if (!props) return data;

    const detectedFigureType = findElementDescendant(figure, isImageElement)
      ? 'image'
      : 'video';

    const figureData = props.figure
      ? Expressions.getRecord(props.figure)
      : null;
    const style = props.style ? Expressions.getRecord(props.style) : null;

    const alignment = getAlignment(figureData?.align ?? null);

    if (detectedFigureType === 'image') {
      const maxWidthExpr = figureData?.maxWidth;
      const maxWidth = maxWidthExpr ? Expressions.getText(maxWidthExpr) : null;

      return ERecord({
        ...props,
        figure: ERecord({
          ...figureData,
          type: EText(detectedFigureType),
          align: EText(alignment),
        }),
        ...(alignment === 'fullwidth' && maxWidth
          ? {
              style: ERecord({
                ...style,
                'max-width': EText(`${maxWidth}px`),
              }),
            }
          : null),
      });
    }

    const maxHeightExpr = figureData?.maxHeight;
    const maxHeight = maxHeightExpr
      ? Expressions.getText(maxHeightExpr) || ''
      : DEFAULT_VIDEO_PADDING_BOTTOM;

    return ERecord({
      ...props,
      figure: ERecord({
        ...figureData,
        type: EText(detectedFigureType),
        align: EText(alignment),
        maxHeight: EText(maxHeight),
      }),
      // copy the padding-bottom property to be an inline style as well
      style: ERecord({
        ...style,
        paddingBottom: EText(maxHeight),
      }),
    });
  })(figure);

export const figcaptionIsPlaceholder = (figCaption: ExpressionElement) => {
  const figureFigcaptionChildren = getChildren(figCaption);

  if (!figureFigcaptionChildren) {
    return false;
  }

  // figcaption can contain links and formatted text
  if (figureFigcaptionChildren.length === 1) {
    const figCaptionChild = figureFigcaptionChildren[0];

    if (figCaptionChild === undefined) return false;

    if (isStringElement(figCaptionChild)) {
      const figCaptionText = getTextValue(figCaptionChild);
      return (
        figCaptionText === FIGCAPTION_PLACEHOLDER_TEXT || figCaptionText === ''
      );
    }
  }

  return false;
};

// Note: this removes all placeholder/empty figcaptions. It should only be used when
// you know the caret is not inside an empty/placeholder figcaption, or if you will
// move the selection after running this.
const removePlaceholderFigcaption = (figure: ExpressionElement) => {
  const figcaption = findElementDescendant(figure, isFigCaption);
  if (!figcaption) return figure;
  if (!figcaptionIsPlaceholder(figcaption)) return figure;
  return updateData((data) => removeElementById(data, figcaption.id))(figure);
};

const enforceFigures = (subtree: ExpressionElement) =>
  updateChildren(
    subtree.id,
    mapArray((child) => {
      if (!isFigure(child)) return child;
      return removePlaceholderFigcaption(ensureFigureHasDefaultData(child));
    })
  )(subtree);

// Unicode newlines can make their way into textNodes via csv import, zapier, or just old CMS content or browser plugins
// This splits them out of the textNodes and converts them to Element line breaks
const convertUnicodeLineBreaksToNodes = (elementAndId: ElementAndId) => {
  const element = getFirst(elementAndId);

  const nextIdAndData = Expressions.everywhereWithState<string>(
    (expr) => (seedId) => {
      const el = Expressions.getElement(expr);
      if (!el) return pair(seedId, expr);
      const children = getChildren(el);
      if (!children) return pair(seedId, expr);

      const newChildren: Array<ExpressionElement> = [];
      let nextId = seedId;

      children.forEach((child) => {
        if (!isStringElement(child)) return;

        const originalValue = Expressions.getText(child.data) || '';

        // This will split the textNode's value into the new TextNodes
        // Empty strings in the array represent a split between two unicode line breaks and can be removed later
        // since we don't need textNodes with eompty strings, but do need them to count the element line breaks we need
        const newTextNodeValues = originalValue
          ? originalValue.split(/\r\n|\r|\n/)
          : null;

        if (newTextNodeValues && newTextNodeValues.length > 1) {
          // If the textNode was split then create new textNodes and line break nodes as needed
          newTextNodeValues.forEach((value) => {
            if (value) {
              // If the value is truthy give it a textNode
              newChildren.push({
                id: nextId,
                type: qualify(Plugins.name('Basic'), Component.name('String')),
                data: EText(value),
              });
              nextId = incrementGuid(nextId);
            }

            const br = {
              id: nextId,
              type: qualify(Plugins.name('Basic'), Component.name('LineBreak')),
              data: ERecord({}),
            };
            nextId = incrementGuid(nextId);

            // All splits are line breaks
            newChildren.push(br);
          });
        }
      });

      const childHadLineBreaks = seedId !== nextId;
      if (!childHadLineBreaks) return pair(seedId, expr);

      return pair(nextId, EElement(setChildren(newChildren)(el)));
    }
  )(element.data)(getSecond(elementAndId));

  const newData = getSecond(nextIdAndData);

  return newData === element.data
    ? elementAndId
    : pair(
        {
          ...element,
          data: newData,
        },
        getFirst(nextIdAndData)
      );
};

const isNotEmpty = (el: ExpressionElement) => {
  if (isStringElement(el) && Expressions.getText(el.data)) return true;
  if (
    isImageElement(el) ||
    isLineBreakElement(el) ||
    isIframeElement(el) ||
    isEmbed(el) ||
    isCodeBlock(el) ||
    isUserComponent(el.type)
  )
    return true;
  if (getChildren(el)?.length) return true;
  return false;
};

// Note: as we remove things their parents may become empty
// Remove empty nodes that should be removed
const removeEmptyNodesFromSubtree = (subtree: ExpressionElement) => {
  let oldSubtree = subtree;
  let newSubtree = subtree;
  // Do it repeatedly in case nodes are incorrectly nested multiple levels deep
  do {
    oldSubtree = newSubtree;
    newSubtree = filterElementTree(oldSubtree, isNotEmpty);
  } while (oldSubtree !== newSubtree);
  return newSubtree;
};

// cleanup enforcement data
const removeTempEnforcementData = updateData(
  Expressions.everywhere(
    matchExpression({
      Element: (el) => {
        const props = Expressions.getRecord(el.data);
        if (!props) return EElement(el);
        const {enforce, ...rest} = props;
        if (!enforce) return EElement(el);
        return EElement({...el, data: ERecord(rest)});
      },
      _: identity,
    })
  )
);

const enforceRichTextStructure = pipe([
  wrapAllFigureContentInFigures,
  convertContentlessFiguresToParagraphs,
  convertFigurelessFigcaptionsToParagraphs,
  wrapAllFigureTextContentInFigcaption,
  wrapSublevelElementsAtTopLevelInParagraphs,
  removeMisplacedLineBreakElements,
  nestListsInListItems,
  replaceTopLevelTyposNestedInListItemsToListItems,
  unpackInvalidNodesInFigcaptionElements,
  convertStrayDivsToTopLevelTypos,
  breakTopLevelTyposToTheTopLevel,
  mergeNewlyWrappedAdjacentParagraphs,
  sortFormatElementHierarchy,
  idPassthrough(mergeAdjacementFormatElements),
  idPassthrough(ensureLinksHaveDefaultData),
  idPassthrough(enforceFigures),
  convertUnicodeLineBreaksToNodes,
  idPassthrough(removeEmptyNodesFromSubtree),
  idPassthrough(removeTempEnforcementData),
]);

/**
 * This enforces some structure on the contentNodes of simple text and rich text elements.
 * It works on an element subtree (ie: the textHolder and its descendants). In the context of
 * enforcing structure on pasted in, or imported (eg via cms import) content, there
 * should be some amount of cleaning to remove "invalid" nodes, and convert that content
 * to Elements that were inserted somewhere in the tree. See ContentEditorClipboardHelper for some
 * examples of this.
 */
export const enforceTagStructure =
  (opts: EnforceOptionsType) =>
  (subtreeAndId: ElementAndId): ElementAndId => {
    const [subtree] = subtreeAndId;
    // Note: The nodes will already have been through some kind of conversion into Elements
    // so they will already be type 1 or 3 nodes and be of a subset of RTE-compatible nodes
    // NOTE: The order that these steps are applied is important
    if (isRichText(subtree)) return enforceRichTextStructure(subtreeAndId);

    if (isTypographic(subtree))
      return pipe([
        idPassthrough(unpackInvalidElementsInSimpleText(opts)),
        sortFormatElementHierarchy,
        idPassthrough(mergeAdjacementFormatElements),
        idPassthrough(ensureLinksHaveDefaultData),
        convertUnicodeLineBreaksToNodes,
        idPassthrough(removeEmptyNodesFromSubtree),
      ])(subtreeAndId);

    return subtreeAndId;
  };

export type TopLevelRichTextRange = [number, number];

const normalizeTopLevelRichTextRange = (
  childrenLength: number,
  range: TopLevelRichTextRange
): TopLevelRichTextRange | null => {
  if (childrenLength === 0) return null;
  const [start, end] = range;
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;

  const lastIndex = childrenLength - 1;
  const clampedStart = clamp(0, lastIndex, start);
  const clampedEnd = clamp(0, lastIndex, end);

  return [
    Math.min(clampedStart, clampedEnd),
    Math.max(clampedStart, clampedEnd),
  ];
};

// Enforces only a top-level child range inside a rich-text subtree. Useful for
// paste flows where we can scope expensive enforcement to the locally changed
// region instead of walking the entire destination RTE.
export const enforceTagStructureScoped =
  (opts: EnforceOptionsType) =>
  (
    subtreeAndId: ElementAndId,
    topLevelRange: TopLevelRichTextRange
  ): ElementAndId => {
    const [subtree, seedId] = subtreeAndId;
    const enforceFull = () => enforceTagStructure(opts)(subtreeAndId);
    if (!isRichText(subtree)) return enforceFull();

    const children = getChildren(subtree) ?? emptyArray;
    const normalizedRange = normalizeTopLevelRichTextRange(
      children.length,
      topLevelRange
    );
    if (!normalizedRange) return enforceFull();

    const [start, end] = normalizedRange;
    if (start === 0 && end === children.length - 1) return enforceFull();

    const scopedSubtree = setChildren(children.slice(start, end + 1))(subtree);
    const [enforcedScopedSubtree, nextId] = enforceTagStructure(opts)(
      pair(scopedSubtree, seedId)
    );

    const enforcedScopedChildren =
      getChildren(enforcedScopedSubtree) ?? emptyArray;
    const mergedChildren = [
      ...children.slice(0, start),
      ...enforcedScopedChildren,
      ...children.slice(end + 1),
    ];

    return [setChildren(mergedChildren)(enforcedScopedSubtree), nextId];
  };

export const enforceRichTextTagStructure = (
  subtree: ExpressionElement,
  seedId: string
): ExpressionElement =>
  getFirst(enforceTagStructure({})(pair(subtree, seedId)));

// Normalizes inline/typographic element trees (bold, italic, links, etc.) after reconstruction.
// Allows links in content — required when translated output may include link elements.
export const enforceInlineTagStructure = (
  subtree: ExpressionElement,
  seedId: string
): ExpressionElement =>
  getFirst(
    enforceTagStructure({allowLinksInContent: true})(pair(subtree, seedId))
  );

// Updates an image element alt text
export const setImageAltText =
  (imageElement: ExpressionElement, altText: string) =>
  (subtree: ExpressionElement): ExpressionElement => {
    const result = Expressions.updateElement(
      imageElement.id,
      updateData((data) =>
        Expressions.setIn(data, ATTR_ALT_PATH, EText(altText))
      ),
      EElement(subtree)
    );

    return Expressions.getElement(result) || subtree;
  };

export const zwjString = (id: string) => ({
  id,
  type: qualify(Plugins.name('Basic'), Component.name('String')),
  data: EText(ZWJ_RAW),
});

/**
 We need to convert `LineBreak` elements to ZWJ text nodes
 so the Rich Text editor lets the user properly
 interact with pasted content.
 */
export const patchLineBreakElements = (
  elementsAndId: Pair<Array<ExpressionElement>, string>
): Pair<Array<ExpressionElement>, string> => {
  return getFirst(elementsAndId).reduce(
    (acc: [Array<ExpressionElement>, string], element: ExpressionElement) => {
      if (isLineBreakElement(element)) {
        const id = acc[1];
        acc[0].push(zwjString(id));
        acc[1] = incrementGuid(id);
      } else {
        acc[0].push(element);
      }

      return acc;
    },
    [[], getSecond(elementsAndId)]
  );
};

export const moveCaretToDeepEndOfNode =
  (nodeId: string) =>
  (subtree: ExpressionElement): RangeSelectionRecordType | null => {
    const rightMostTextOrLineBreak = getRightDeepestTextOrLineBreak(
      subtree,
      nodeId
    );
    if (rightMostTextOrLineBreak === null) return null;
    const rightMostTextOrLineBreakId = Expressions.getElementId(
      rightMostTextOrLineBreak
    );

    if (isStringElement(rightMostTextOrLineBreak)) {
      const stringElementValue =
        Expressions.getText(rightMostTextOrLineBreak.data) ?? '';
      if (stringElementValue === null) return null;
      const lastCharOffset = stringElementValue.length;

      return RangeSelectionRecord({
        anchorNodeId: rightMostTextOrLineBreakId,
        focusNodeId: rightMostTextOrLineBreakId,
        anchorOffset: lastCharOffset,
        focusOffset: lastCharOffset,
        leftToRight: true,
      });

      // is in linebreak so parent textholder (.e.g paragraph, figcaption etc) should be the selection, offset to where the linebreak is
    } else {
      const parentElement =
        Expressions.getParent(EElement(subtree), rightMostTextOrLineBreakId) ??
        null;

      if (parentElement === null) return null;

      const parentElementId = parentElement.id;
      const lastCharOffset = getChildren(parentElement)?.length ?? 0;

      return RangeSelectionRecord({
        anchorNodeId: parentElementId,
        focusNodeId: parentElementId,
        anchorOffset: lastCharOffset,
        focusOffset: lastCharOffset,
        leftToRight: true,
      });
    }
  };

export const selectStartOfTextNode = (
  elementId: string,
  root: ExpressionElement
): RangeSelectionRecordType | null => {
  const leftMostTextOrLineBreakNode = getLeftDeepestTextOrLineBreak(
    root,
    elementId
  );
  if (
    leftMostTextOrLineBreakNode &&
    isStringElement(leftMostTextOrLineBreakNode)
  ) {
    const textNodeId = leftMostTextOrLineBreakNode.id;
    return RangeSelectionRecord({
      anchorNodeId: textNodeId,
      focusNodeId: textNodeId,
      anchorOffset: 0,
      focusOffset: 0,
      leftToRight: true,
    });
  }
  return null;
};

export const moveCaretToDeepStartOfNode =
  (elementId: string) =>
  (root: ExpressionElement): RangeSelectionRecordType | null => {
    const leftMostTextOrLineBreakNode = getLeftDeepestTextOrLineBreak(
      root,
      elementId
    );
    if (
      leftMostTextOrLineBreakNode &&
      isStringElement(leftMostTextOrLineBreakNode)
    ) {
      const textNodeId = leftMostTextOrLineBreakNode.id;
      return RangeSelectionRecord({
        anchorNodeId: textNodeId,
        focusNodeId: textNodeId,
        anchorOffset: 0,
        focusOffset: 0,
        leftToRight: true,
      });
    } else {
      // There is an edge case in the Data Manager where the subtree will have no
      // depth after clicking an empty rich text field for the second time. This
      // case guards against it to allow the user to click and create some
      // temporary content.
      const parentNodeId = leftMostTextOrLineBreakNode
        ? Expressions.getParent(EElement(root), leftMostTextOrLineBreakNode.id)
            ?.id
        : elementId;

      return parentNodeId
        ? RangeSelectionRecord({
            anchorNodeId: parentNodeId,
            focusNodeId: parentNodeId,
            anchorOffset: 0,
            focusOffset: 0,
            leftToRight: true,
          })
        : null;
    }
  };

export const caretIsAtTheEndOfTheNode =
  (elementId: string) =>
  (state: ContentEditorActive): boolean => {
    const {subtree, selection} = state;
    const rangeSelection = assertRangeSelectionRecordType(selection);
    if (!rangeSelection) return false;
    const elementSubtreeChildren = getChildren(subtree);
    if (elementSubtreeChildren === undefined) return false;
    const rootId = Expressions.getElementId(subtree);
    const element = Expressions.getElementById(EElement(subtree), elementId);
    if (element === null) return false;

    // If checking the root element, we need to examine its last child
    if (elementId === rootId) {
      const children = getChildren(element);
      if (children === undefined) return false;

      const lastChild = children[children.length - 1];

      if (!lastChild) return false;

      // If the last child is a figure without a figcaption,
      // we know the caret can't be at the end
      if (
        isFigure(lastChild) &&
        !getFigcaptionForFigure(subtree, Expressions.getElementId(lastChild))
      ) {
        return false;
      }
    }

    const selectionIfAtEnd = moveCaretToDeepEndOfNode(elementId)(subtree);

    if (selectionIfAtEnd === null) return false;

    return (
      selectionIfAtEnd.anchorNodeId === rangeSelection.anchorNodeId &&
      selectionIfAtEnd.anchorOffset === rangeSelection.anchorOffset
    );
  };

export const caretIsAtTheStartOfTheNode =
  (elementId: string) =>
  (state: ContentEditorActive): boolean => {
    const {subtree, selection} = state;
    const elementSubtreeChildren = getChildren(subtree);
    if (elementSubtreeChildren === undefined) return false;
    const rootId = Expressions.getElementId(subtree);
    const element = Expressions.getElementById(EElement(subtree), elementId);
    if (element === null) return false;

    // If we're checking whether the caret is at the start of the whole subtree
    // then figures come into play. Since we only care about/use this function on
    // range selections, if the first child of the subtree is a figure then we
    // know the caret is not at the start of the root element
    if (elementId === rootId && isFigure(element)) {
      return false;
    }

    const selectionIfAtStart = moveCaretToDeepStartOfNode(elementId)(subtree);

    if (selectionIfAtStart === null) return false;

    return (
      selection.type === 'Range' &&
      selectionIfAtStart.anchorNodeId === selection.anchorNodeId &&
      selectionIfAtStart.anchorOffset === selection.anchorOffset
    );
  };

export const caretIsAtTheEndOfALink = (state: ContentEditorActive): boolean => {
  const {subtree, selection} = state;
  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (rangeSelection === null) return false;
  const closestAnchor = Expressions.closestElement(
    EElement(subtree),
    rangeSelection.anchorNodeId,
    isLinkElement
  );
  return closestAnchor
    ? caretIsAtTheEndOfTheNode(closestAnchor.id)(state)
    : false;
};

// sets the selection at the very end of the element - selecting the start of the root element as a fallback.
export const resetSelection = (state: ContentEditorActive) => {
  const subtreeId = state.subtree.id;
  return setSelection(
    moveCaretToDeepEndOfNode(subtreeId)(state.subtree) ||
      RangeSelectionRecord({
        anchorNodeId: subtreeId,
        focusNodeId: subtreeId,
        anchorOffset: 0,
        focusOffset: 0,
        leftToRight: true,
      })
  )(state);
};

// For text selections that start inside a figcaption and end outside,
// this confines the outside edge to be inside that same figcaption.
// This matches the legacy RTE ux but is no longer a technical requirement with the new ContentEditor
export const handleFigcaptionSelection =
  (subtree: ExpressionElement) =>
  (selection: SelectionRecordType): SelectionRecordType => {
    const rangeSelection = assertRangeSelectionRecordType(selection);
    if (!rangeSelection) return selection;
    const {leftNodeId, rightNodeId} = getLeftRightPosition(rangeSelection);
    const leftNodeFigcaption = Expressions.closestElement(
      EElement(subtree),
      leftNodeId,
      isFigCaption
    );
    const rightNodeFigcaption = Expressions.closestElement(
      EElement(subtree),
      rightNodeId,
      isFigCaption
    );
    const selectionAcrossMultipleFigcaptions =
      leftNodeFigcaption &&
      rightNodeFigcaption &&
      leftNodeFigcaption.id !== rightNodeFigcaption.id;

    if (
      (leftNodeFigcaption && !rightNodeFigcaption) ||
      selectionAcrossMultipleFigcaptions
    ) {
      const collapsedSelectionAtEndOfFigcaption = moveCaretToDeepEndOfNode(
        leftNodeFigcaption.id
      )(subtree);
      if (!collapsedSelectionAtEndOfFigcaption) return selection;
      const {rightNodeId: newRightNodeId, rightOffset: newRightOffset} =
        getLeftRightPosition(collapsedSelectionAtEndOfFigcaption);
      // If the left edge of the selection is in a figcaption and the right edge
      // isn't, or if the left and right edges are in two different figcaptions,
      // confine the selection within the left figcaption.
      return setRightEndOfSelection(
        newRightNodeId,
        newRightOffset
      )(rangeSelection);
    }

    if (!leftNodeFigcaption && rightNodeFigcaption) {
      // If the right edge of the selection is in a figcaption and the left edge
      // isn't, confine the selection within the figcaption.
      const collapsedSelectionAtStartOfFigcaption = moveCaretToDeepStartOfNode(
        rightNodeFigcaption.id
      )(subtree);
      if (!collapsedSelectionAtStartOfFigcaption) return selection;
      const {leftNodeId: newLeftNodeId, leftOffset: newLeftOffset} =
        getLeftRightPosition(collapsedSelectionAtStartOfFigcaption);
      return setLeftEndOfSelection(
        newLeftNodeId,
        newLeftOffset
      )(rangeSelection);
    }

    // otherwise do nothing.
    return selection;
  };

// When the left or right end of the selection ends 'lost' inside a figure
// ie: somewhere in the content div or otherwise outside the figcaption then
// just select the figure with a node selection.
export const handleFigureEndingSelection =
  (subtree: ExpressionElement) =>
  (selection: SelectionRecordType): SelectionRecordType => {
    const rangeSelection = assertRangeSelectionRecordType(selection);
    if (!rangeSelection) return selection;
    const {leftNodeId, rightNodeId} = getLeftRightPosition(rangeSelection);
    const root = EElement(subtree);
    const leftNodeFigure = getClosestFigureElement(root, leftNodeId);
    const rightNodeFigure = getClosestFigureElement(root, rightNodeId);
    const leftNodeFigcaption = Expressions.closestElement(
      root,
      leftNodeId,
      isFigCaption
    );
    const rightNodeFigcaption = Expressions.closestElement(
      root,
      rightNodeId,
      isFigCaption
    );
    // Is either edge of the RangeSelection in the figure but not in a figcaption
    const leftSideLostInFigure = leftNodeFigure && !leftNodeFigcaption;
    const rightSideLostInFigure = rightNodeFigure && !rightNodeFigcaption;
    const leftSideFigureToSelectId =
      leftSideLostInFigure && leftNodeFigure ? leftNodeFigure.id : undefined;
    const rightSideFigureToSelectId =
      rightSideLostInFigure && rightNodeFigure ? rightNodeFigure.id : undefined;
    // If both sides are lost in figures, favor the left side.
    const newNodeToSelectId =
      leftSideFigureToSelectId || rightSideFigureToSelectId;

    if (newNodeToSelectId) {
      return NodeSelectionRecord({nodeId: newNodeToSelectId});
    }

    return selection;
  };

// Firefox likes to make selections relative to the subtree in some 'everything
// is selected' situations. This makes sure those selections are bumped in to
// be based on the textNode/lineBreak parent level.
export const handleRangeSelectionAtSubtreeRoot =
  (subtree: ExpressionElement) =>
  (selection: SelectionRecordType): SelectionRecordType => {
    const rangeSelection = assertRangeSelectionRecordType(selection);
    if (!rangeSelection) return selection;
    const selectionIsTopLevelNode =
      subtree.id === rangeSelection.anchorNodeId &&
      subtree.id === rangeSelection.focusNodeId;

    // Allowing this in non-rich text solves an issue where you can not select all and delete in Firefox.
    // However, it also causes an issue when the selection is collapsed and in a line break.
    // So only run when the selection is not collapsed.
    if (
      (isRichText(subtree) && selectionIsTopLevelNode) ||
      (!isCollapsed(rangeSelection) && selectionIsTopLevelNode)
    ) {
      const movedStart = moveCaretToDeepStartOfNode(subtree.id)(subtree);
      const movedEnd = moveCaretToDeepEndOfNode(subtree.id)(subtree);
      if (!movedStart || !movedEnd) return selection;
      const {anchorNodeId, anchorOffset} = movedStart;
      const {focusNodeId, focusOffset} = movedEnd;

      return pipe([
        setLeftEndOfSelection(anchorNodeId, anchorOffset),
        setRightEndOfSelection(focusNodeId, focusOffset),
      ])(rangeSelection);
    }

    return selection;
  };

export const getFigcaptionForFigure = (
  subtree: ExpressionElement,
  figureId: string
): ExpressionElement | null => {
  const figure = Expressions.getElementById(EElement(subtree), figureId);
  if (figure === null) return null;
  return findElementDescendant(figure, isFigCaption) ?? null;
};

export const setCustomClassOnTypoNode = (
  newCustomClass: string | null | undefined,
  state: ContentEditorActive
) => {
  const {subtree, selection} = state;
  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (!rangeSelection) return state;
  const typoNode = getClosestTopLevelTypoElement(
    rangeSelection.anchorNodeId,
    subtree
  );
  if (typoNode === null) return state;

  return updateSubtree(
    updateElementById(
      typoNode.id,
      updateData((data) => {
        const props = Expressions.getRecord(data);
        if (!props) return data;
        const {customclass, ...rest} = props;
        if (!customclass && !newCustomClass) return data;
        if (customclass && Expressions.getText(customclass) === newCustomClass)
          return data;
        if (!newCustomClass) return ERecord(rest);
        return ERecord({...rest, customclass: EText(newCustomClass)});
      })
    )
  )(state);
};

/**
 * Splits a node into two parts at the current selection point. Creates two separate nodes - one
 * containing everything up to the split point, and another containing everything after.
 * `targetNodeId` is the ID of the node to split (e.g. paragraph, list item).
 */
export const splitNodeOnCollapsedRangeInTextNode =
  (targetNodeId: string) =>
  (
    stateAndNextId: ContentEditorActiveAndNextId
  ): ContentEditorActiveAndNextId => {
    const [state, nextId] = stateAndNextId;
    const {subtree, selection} = state;
    const rangeSelection = assertRangeSelectionRecordType(selection);

    if (!rangeSelection || !subtree) {
      return stateAndNextId;
    }

    const {leftNodeId, leftOffset} = getLeftRightPosition(rangeSelection);
    const textElement = Expressions.getElementById(subtree.data, leftNodeId);

    if (!textElement || !isStringElement(textElement)) {
      throw new Error(
        'splitNodeOnCollapsedRangeInTextNode requires a text node at the selection anchor'
      );
    }

    const textContent = Expressions.getText(textElement.data) || '';
    const leftText = textContent.substring(0, leftOffset) || ZWJ_RAW;
    const rightText = textContent.substring(leftOffset) || ZWJ_RAW;

    const updatedSubtreeExpr = Expressions.updateElement(
      leftNodeId,
      updateData(() => EText(leftText)),
      EElement(subtree)
    );

    if (!updatedSubtreeExpr) {
      return stateAndNextId;
    }

    const updatedSubtree = Expressions.getElement(updatedSubtreeExpr);
    if (!updatedSubtree) {
      return stateAndNextId;
    }

    const targetElement = Expressions.getElementById(
      updatedSubtree.data,
      targetNodeId
    );
    if (!targetElement) {
      return stateAndNextId;
    }

    const leftElement = rightTrimSubtree(targetElement, leftNodeId);
    const rightElement = leftTrimSubtree(targetElement, leftNodeId);

    if (!leftElement || !rightElement) {
      return stateAndNextId;
    }

    const rightElementWithText = Expressions.updateElement(
      leftNodeId,
      updateData(() => EText(rightText)),
      EElement(rightElement)
    );

    if (!rightElementWithText) {
      return stateAndNextId;
    }

    let currentId = nextId;
    const rightElementWithNewIds = Expressions.everywhere(
      matchExpression({
        Element: (el: ExpressionElement) => {
          const newId = currentId;
          currentId = incrementGuid(currentId);
          return EElement({...el, id: newId});
        },
        _: identity,
      })
    )(rightElementWithText);

    const finalRightElement = Expressions.getElement(rightElementWithNewIds);

    if (!updatedSubtree || !finalRightElement) {
      return [state, currentId];
    }

    const finalSubtreeExpr = addElementAtAnchorById(
      EElement(replaceElement(targetNodeId, leftElement)(updatedSubtree)),
      finalRightElement,
      targetNodeId,
      'after'
    );

    const newSelection = moveCaretToDeepStartOfNode(finalRightElement.id)(
      finalRightElement
    );

    const finalSubtree = Expressions.getElement(finalSubtreeExpr);

    if (!newSelection || !finalSubtree) {
      return [state, currentId];
    }

    return [
      state.set('subtree', finalSubtree).set('selection', newSelection),
      currentId,
    ];
  };

/**
 * Trims a subtree from a given text node either from the left or right side.
 * If rightTrim is true, keeps elements up to and including the text node.
 * If rightTrim is false, keeps elements from the text node onwards.
 */
const trimSubtree = (
  subtree: ExpressionElement,
  textNodeId: string,
  rightTrim: boolean
): ExpressionElement => {
  const subtreeExpr = EElement(subtree);
  const path = getKeyPathToElement(subtreeExpr, textNodeId);
  if (!path) return subtree;

  const textNode = Expressions.getElementById(subtree.data, textNodeId);
  if (!textNode || !isStringElement(textNode)) return subtree;

  const pathIndexes = _extractPathIndexes(path);

  const parent = Expressions.getParent(subtreeExpr, textNodeId);
  if (!parent) return subtree;

  let currentPath = getKeyPathToElement(subtreeExpr, parent.id);
  if (!currentPath) return subtree;

  let result = subtree;

  pathIndexes.forEach((index) => {
    if (!currentPath) return;

    const element = _getElementAtPath(result, currentPath);
    if (!element) return;

    const children = getChildren(element);
    if (!children) return;

    const trimmedChildren = _trimChildren(children, index, rightTrim);

    result = _updateChildrenAtPath(result, currentPath, trimmedChildren);

    currentPath = _getParentPath(result, element.id);
  });

  return result;
};

/**
 * Trims a subtree from the left side starting from the specified text node.
 */
export const leftTrimSubtree = (
  subtree: ExpressionElement,
  textNodeId: string
): ExpressionElement => {
  return trimSubtree(subtree, textNodeId, false);
};

/**
 * Trims a subtree from the right side up to the specified text node.
 */
export const rightTrimSubtree = (
  subtree: ExpressionElement,
  textNodeId: string
): ExpressionElement => {
  return trimSubtree(subtree, textNodeId, true);
};

// returns a subtree, removing the text between the start and end offset
const removeTextNodeValueSlice =
  (start: number, end: number) => (data: Expression) => {
    const value = Expressions.getText(data) || '';
    const charsToBeRemoved = value.slice(start, end);
    // If the only char selected is a ZWJ then prevent it. Users shouldn't be
    // able to manually remove ZWJ characters. They're added/removed as needed
    // to get around certain browser quirks
    if (charsToBeRemoved === ZWJ_RAW) return data;

    return EText(value.slice(0, start) + value.slice(end));
  };

export const getSelectedSubtree = (
  state: ContentEditorActive
): ExpressionElement => {
  const {subtree, selection} = state;
  if (selection.type === 'Range') {
    const {leftNodeId, rightNodeId, leftOffset, rightOffset} =
      getLeftRightPosition(selection);

    const leftTrimmedSubtree = leftTrimSubtree(subtree, leftNodeId);
    const selectedSubtree = rightTrimSubtree(leftTrimmedSubtree, rightNodeId);

    const leftNode = Expressions.getElementById(
      EElement(selectedSubtree),
      leftNodeId
    );
    const rightNode = Expressions.getElementById(
      EElement(selectedSubtree),
      rightNodeId
    );

    if (
      leftNodeId === rightNodeId &&
      leftNode &&
      rightNode &&
      isStringElement(leftNode) &&
      isStringElement(rightNode)
    )
      return updateElementById(
        leftNodeId,
        updateData((data) =>
          EText(
            (Expressions.getText(data) || '').slice(leftOffset, rightOffset)
          )
        )
      )(selectedSubtree);

    if (leftNodeId !== rightNodeId) {
      const newSelectedSubtree =
        leftNode && isStringElement(leftNode)
          ? updateElementById(
              leftNodeId,
              updateData(removeTextNodeValueSlice(0, leftOffset))
            )(selectedSubtree)
          : selectedSubtree;
      return rightNode && isStringElement(rightNode)
        ? updateElementById(
            rightNodeId,
            updateData(
              removeTextNodeValueSlice(
                rightOffset,
                (Expressions.getText(rightNode.data) || '').length
              )
            )
          )(newSelectedSubtree)
        : newSelectedSubtree;
    }
  } else if (selection?.nodeId) {
    const figure = Expressions.getElementById(
      EElement(subtree),
      selection.nodeId
    );
    return {
      id: 'RichText',
      type: qualify(Plugins.name('Basic'), Component.name('RichText')),
      data: ERecord({
        children: EList(figure ? [EElement(figure)] : []),
        tag: EText('div'),
        rich: EBoolean(true),
      }),
    };
  }
  return subtree;
};

// Adds a new heading element via the text shortcut in Rich Text
export const getCustomClassOnTypoElement = (
  state: ContentEditorActive
): string => {
  const selection = assertRangeSelectionRecordType(state.selection);
  if (!selection) return '';
  const typoElement = getClosestTopLevelTypoElement(
    selection.anchorNodeId,
    state.subtree
  );
  if (!typoElement) return '';
  const expr = Expressions.getRecord(typoElement.data)?.customclass;
  if (!expr) return '';
  return Expressions.getText(expr) || '';
};

const _extractPathIndexes = (path: ExpressionPath): number[] => {
  return path
    .reduce<number[]>((indexes, p) => {
      if (p.in === 'List') {
        indexes.push(p.index);
      }
      return indexes;
    }, [])
    .reverse();
};

const _getElementAtPath = (
  subtree: ExpressionElement,
  path: ExpressionPath
): ExpressionElement | null => {
  const elementExpr = Expressions.getIn(EElement(subtree), path);
  return elementExpr ? Expressions.getElement(elementExpr) : null;
};

const _trimChildren = (
  children: ExpressionElement[],
  index: number,
  rightTrim: boolean
): ExpressionElement[] => {
  return rightTrim ? children.slice(0, index + 1) : children.slice(index);
};

const _updateChildrenAtPath = (
  subtree: ExpressionElement,
  path: ExpressionPath,
  children: ExpressionElement[]
): ExpressionElement => {
  const updated = Expressions.updateIn(
    EElement(subtree),
    [...path, {in: 'Element'}, {in: 'Record', at: 'children'}],
    () => EList(children.map(EElement))
  );
  return updated ? Expressions.getElement(updated) || subtree : subtree;
};

const _getParentPath = (
  subtree: ExpressionElement,
  elementId: string
): ExpressionPath | null => {
  const subtreeExpr = EElement(subtree);
  const parent = Expressions.getParent(subtreeExpr, elementId);
  return parent ? getKeyPathToElement(subtreeExpr, parent.id) : null;
};
